// Code Injection Component Templates
// Reorganized into granular categories with enhanced selection options

import { ComponentTemplate, ComponentCategory } from '@/types/codeInjection';

// =============================================================================
// MENU ITEM EFFECTS
// =============================================================================

// Menu Item Movement Templates (Single Selection - Base transform effects)
export const menuItemMovement: ComponentTemplate[] = [
  {
    id: 'menu-item-lift',
    category: 'menuItemMovement',
    name: 'Subtle Lift',
    description: 'Gentle upward movement on hover',
    cssTemplate: `
      /* Only apply hover to in-stock items */
      .item__DishComponent-wkeq8p-0.cursor:hover {
        transform: translateY(calc(-8px * {{movementStrength}}));
      }
      
      @media (max-width: 768px) {
        .item__DishComponent-wkeq8p-0.cursor:hover {
          transform: translateY(calc(-4px * {{movementStrength}}));
        }
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      movementStrength: '{{movementStrength}}'
    }
  },
  {
    id: 'menu-item-scale',
    category: 'menuItemMovement',
    name: 'Scale Effect',
    description: 'Gentle scaling on hover',
    cssTemplate: `
      /* Only apply hover to in-stock items */
      .item__DishComponent-wkeq8p-0.cursor:hover {
        transform: scale(calc(1 + 0.03 * {{movementStrength}}));
      }
      
      @media (max-width: 768px) {
        .item__DishComponent-wkeq8p-0.cursor:hover {
          transform: scale(calc(1 + 0.01 * {{movementStrength}}));
        }
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      movementStrength: '{{movementStrength}}'
    }
  },
  {
    id: 'menu-item-tilt',
    category: 'menuItemMovement',
    name: 'Tilt Effect',
    description: 'Subtle tilt rotation with lift on hover',
    cssTemplate: `
      /* Only apply hover to in-stock items */
      .item__DishComponent-wkeq8p-0.cursor:hover {
        transform: rotate(calc(1.5deg * {{movementStrength}})) translateY(calc(-3px * {{movementStrength}}));
      }
      
      @media (max-width: 768px) {
        .item__DishComponent-wkeq8p-0.cursor:hover {
          transform: rotate(calc(0.5deg * {{movementStrength}})) translateY(calc(-2px * {{movementStrength}}));
        }
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      movementStrength: '{{movementStrength}}'
    }
  }
];

// Menu Item Glow Templates (Multi-Selection - Shadow and glow effects)
export const menuItemGlow: ComponentTemplate[] = [
  {
    id: 'menu-item-subtle-shadow',
    category: 'menuItemGlow',
    name: 'Subtle Shadow',
    description: 'Multiple layer subtle shadow enhancement',
    cssTemplate: `
      .item__DishComponent-wkeq8p-0.cursor:hover {
        box-shadow: 0 15px 35px rgba(0, 0, 0, calc(0.15 * {{glowStrength}})), 0 5px 15px rgba(0, 0, 0, calc(0.1 * {{glowStrength}}));
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      glowStrength: '{{glowStrength}}'
    }
  },
  {
    id: 'menu-item-medium-shadow',
    category: 'menuItemGlow',
    name: 'Medium Shadow',
    description: 'Enhanced multi-layer shadow effect',
    cssTemplate: `
      .item__DishComponent-wkeq8p-0.cursor:hover {
        box-shadow: 0 20px 40px rgba(0, 0, 0, calc(0.2 * {{glowStrength}})), 0 8px 20px rgba(0, 0, 0, calc(0.15 * {{glowStrength}}));
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      glowStrength: '{{glowStrength}}'
    }
  },
  {
    id: 'menu-item-brand-glow',
    category: 'menuItemGlow',
    name: 'Brand Glow',
    description: 'Multiple shadow layers with brand colour glow',
    cssTemplate: `
      .item__DishComponent-wkeq8p-0.cursor:hover {
        box-shadow: 0 15px 35px rgba(0, 0, 0, calc(0.15 * {{glowStrength}})), 
                    0 5px 15px rgba(0, 0, 0, calc(0.1 * {{glowStrength}})),
                    0 0 25px rgba({{primaryColorRgb}}, calc(0.3 * {{glowStrength}}));
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      glowStrength: '{{glowStrength}}'
    }
  },
  {
    id: 'menu-item-strong-glow',
    category: 'menuItemGlow',
    name: 'Strong Glow',
    description: 'Intense multi-layer glowing effect',
    cssTemplate: `
      .item__DishComponent-wkeq8p-0.cursor:hover {
        box-shadow: 0 20px 40px rgba(0, 0, 0, calc(0.25 * {{glowStrength}})), 
                    0 8px 20px rgba(0, 0, 0, calc(0.15 * {{glowStrength}})),
                    0 0 30px rgba({{primaryColorRgb}}, calc(0.4 * {{glowStrength}})),
                    0 0 60px rgba({{primaryColorRgb}}, calc(0.2 * {{glowStrength}}));
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      glowStrength: '{{glowStrength}}'
    }
  },
  {
    id: 'menu-item-dual-brand-glow',
    category: 'menuItemGlow',
    name: 'Dual Brand Glow',
    description: 'Premium dual-colour brand glow effect',
    cssTemplate: `
      .item__DishComponent-wkeq8p-0.cursor:hover {
        box-shadow: 0 15px 35px rgba(0, 0, 0, calc(0.15 * {{glowStrength}})), 
                    0 5px 15px rgba(0, 0, 0, calc(0.1 * {{glowStrength}})),
                    0 0 25px rgba({{primaryColorRgb}}, calc(0.4 * {{glowStrength}})),
                    0 0 40px rgba({{secondaryColorRgb}}, calc(0.2 * {{glowStrength}}));
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      secondaryColorRgb: '{{secondaryColorRgb}}',
      glowStrength: '{{glowStrength}}'
    }
  }
];

// Menu Item Image Effects Templates (Multi-Selection - Image-specific effects)
export const menuItemImageEffects: ComponentTemplate[] = [
  {
    id: 'menu-item-image-zoom',
    category: 'menuItemImageEffects',
    name: 'Image Zoom',
    description: 'Smooth zoom image on hover',
    cssTemplate: `
      .item__Image-wkeq8p-1 {
        transform: scale(1);
        transition: transform 0.5s ease;
      }
      
      .item__DishComponent-wkeq8p-0.cursor:hover .item__Image-wkeq8p-1 {
        transform: scale(1.1);
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}'
    }
  },
  {
    id: 'menu-item-card-overlay-primary',
    category: 'menuItemImageEffects',
    name: 'Card Overlay',
    description: 'Subtle brand colour overlay on the entire card',
    cssTemplate: `
      .item__DishComponent-wkeq8p-0 {
        position: relative;
        overflow: hidden;
      }
      
      .item__DishComponent-wkeq8p-0::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba({{primaryColorRgb}}, 0);
        transition: background 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        pointer-events: none;
        z-index: 1;
      }
      
      .item__DishComponent-wkeq8p-0.cursor:hover::before {
        background: rgba({{primaryColorRgb}}, {{overlayStrength}});
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      overlayStrength: '{{overlayStrength}}'
    }
  },
  {
    id: 'menu-item-image-gradient',
    category: 'menuItemImageEffects',
    name: 'Image Gradient',
    description: 'Gradient overlay effects on images',
    isGrouped: true,
    subTemplates: [
      {
        id: 'menu-item-image-gradient-shadow',
        name: 'Shadow Effect',
        description: 'Bottom gradient shadow overlay for better text readability',
        cssTemplate: `
          .item__Image-wkeq8p-1 {
            position: relative;
            overflow: hidden;
          }
          
          .item__Image-wkeq8p-1:before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(to bottom, rgba(0,0,0,0) 60%, rgba(0,0,0,{{overlayStrength}}) 100%);
            opacity: 0;
            transition: opacity 0.5s ease;
            z-index: 1;
            pointer-events: none;
          }
          
          .item__DishComponent-wkeq8p-0.cursor:hover .item__Image-wkeq8p-1:before {
            opacity: 1;
          }
        `,
        variables: {
          animationDuration: '{{animationDuration}}',
          overlayStrength: '{{overlayStrength}}'
        }
      },
      {
        id: 'menu-item-image-gradient-primary',
        name: 'Primary Colour',
        description: 'Primary brand colour gradient overlay (transparent to primary)',
        cssTemplate: `
          .item__Image-wkeq8p-1 {
            position: relative;
            overflow: hidden;
          }
          
          .item__Image-wkeq8p-1:before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(to bottom, rgba({{primaryColorRgb}}, 0) 50%, rgba({{primaryColorRgb}}, {{overlayStrength}}) 100%);
            opacity: 0;
            transition: opacity 0.5s ease;
            z-index: 1;
            pointer-events: none;
          }
          
          .item__DishComponent-wkeq8p-0.cursor:hover .item__Image-wkeq8p-1:before {
            opacity: 1;
          }
        `,
        variables: {
          animationDuration: '{{animationDuration}}',
          primaryColorRgb: '{{primaryColorRgb}}',
          overlayStrength: '{{overlayStrength}}'
        }
      },
      {
        id: 'menu-item-image-gradient-secondary',
        name: 'Secondary Colour',
        description: 'Secondary brand colour gradient overlay (transparent to secondary)',
        cssTemplate: `
          .item__Image-wkeq8p-1 {
            position: relative;
            overflow: hidden;
          }
          
          .item__Image-wkeq8p-1:before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(to bottom, rgba({{secondaryColorRgb}}, 0) 50%, rgba({{secondaryColorRgb}}, {{overlayStrength}}) 100%);
            opacity: 0;
            transition: opacity 0.5s ease;
            z-index: 1;
            pointer-events: none;
          }
          
          .item__DishComponent-wkeq8p-0.cursor:hover .item__Image-wkeq8p-1:before {
            opacity: 1;
          }
        `,
        variables: {
          animationDuration: '{{animationDuration}}',
          secondaryColorRgb: '{{secondaryColorRgb}}',
          overlayStrength: '{{overlayStrength}}'
        }
      },
      {
        id: 'menu-item-image-gradient-primary-to-secondary',
        name: 'Primary to Secondary',
        description: 'Primary to secondary brand colour gradient overlay',
        cssTemplate: `
          .item__Image-wkeq8p-1 {
            position: relative;
            overflow: hidden;
          }
          
          .item__Image-wkeq8p-1:before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(to bottom, rgba({{primaryColorRgb}}, {{overlayStrength}}) 0%, rgba({{secondaryColorRgb}}, calc({{overlayStrength}} + 0.2)) 100%);
            opacity: 0;
            transition: opacity 0.5s ease;
            z-index: 1;
            pointer-events: none;
          }
          
          .item__DishComponent-wkeq8p-0.cursor:hover .item__Image-wkeq8p-1:before {
            opacity: 1;
          }
        `,
        variables: {
          animationDuration: '{{animationDuration}}',
          primaryColorRgb: '{{primaryColorRgb}}',
          secondaryColorRgb: '{{secondaryColorRgb}}',
          overlayStrength: '{{overlayStrength}}'
        }
      },
      {
        id: 'menu-item-image-gradient-secondary-to-primary',
        name: 'Secondary to Primary',
        description: 'Secondary to primary brand colour gradient overlay',
        cssTemplate: `
          .item__Image-wkeq8p-1 {
            position: relative;
            overflow: hidden;
          }
          
          .item__Image-wkeq8p-1:before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(to bottom, rgba({{secondaryColorRgb}}, {{overlayStrength}}) 0%, rgba({{primaryColorRgb}}, calc({{overlayStrength}} + 0.2)) 100%);
            opacity: 0;
            transition: opacity 0.5s ease;
            z-index: 1;
            pointer-events: none;
          }
          
          .item__DishComponent-wkeq8p-0.cursor:hover .item__Image-wkeq8p-1:before {
            opacity: 1;
          }
        `,
        variables: {
          animationDuration: '{{animationDuration}}',
          primaryColorRgb: '{{primaryColorRgb}}',
          secondaryColorRgb: '{{secondaryColorRgb}}',
          overlayStrength: '{{overlayStrength}}'
        }
      }
    ],
    cssTemplate: ``, // Placeholder - will use selected subTemplate
    variables: {}
  }
];

// Menu Item Text Effects Templates (Multi-Selection - Text styling)
export const menuItemTextEffects: ComponentTemplate[] = [
  {
    id: 'menu-item-title-style',
    category: 'menuItemTextEffects',
    name: 'Static Dish Title Enhancements',
    description: 'Fixed text styling without hover effects',
    cssTemplate: `
      .item__Content-wkeq8p-4 h2 {
        color: {{menuItemTextRestColor}};
        font-size: {{menuItemTextRestFontSize}}px;
        font-weight: {{menuItemTextRestFontWeight}};
        font-style: {{menuItemTextRestFontStyle}};
        text-transform: {{menuItemTextRestTextTransform}};
        letter-spacing: {{menuItemTextRestLetterSpacing}}px;
        text-shadow: {{menuItemTextRestTextShadow}};
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      menuItemTextRestColor: '{{menuItemTextRestColor}}',
      menuItemTextRestFontSize: '{{menuItemTextRestFontSize}}',
      menuItemTextRestFontWeight: '{{menuItemTextRestFontWeight}}',
      menuItemTextRestFontStyle: '{{menuItemTextRestFontStyle}}',
      menuItemTextRestTextTransform: '{{menuItemTextRestTextTransform}}',
      menuItemTextRestLetterSpacing: '{{menuItemTextRestLetterSpacing}}',
      menuItemTextRestTextShadow: '{{menuItemTextRestTextShadow}}'
    }
  },
  {
    id: 'menu-item-title-color',
    category: 'menuItemTextEffects',
    name: 'Dynamic Dish Title Enhancements',
    description: 'Full text styling with hover effects and transitions',
    cssTemplate: `
      .item__Content-wkeq8p-4 h2 {
        color: {{menuItemTextRestColor}};
        font-size: {{menuItemTextRestFontSize}}px;
        font-weight: {{menuItemTextRestFontWeight}};
        font-style: {{menuItemTextRestFontStyle}};
        text-transform: {{menuItemTextRestTextTransform}};
        letter-spacing: {{menuItemTextRestLetterSpacing}}px;
        text-shadow: {{menuItemTextRestTextShadow}};
        transition: all 0.5s ease;
      }
      
      .item__DishComponent-wkeq8p-0.cursor:hover .item__Content-wkeq8p-4 h2 {
        color: {{menuItemTextHoverColor}};
        font-size: {{menuItemTextHoverFontSize}}px;
        font-weight: {{menuItemTextHoverFontWeight}};
        font-style: {{menuItemTextHoverFontStyle}};
        text-transform: {{menuItemTextHoverTextTransform}};
        letter-spacing: {{menuItemTextHoverLetterSpacing}}px;
        text-shadow: {{menuItemTextHoverTextShadow}};
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      menuItemTextRestColor: '{{menuItemTextRestColor}}',
      menuItemTextRestFontSize: '{{menuItemTextRestFontSize}}',
      menuItemTextRestFontWeight: '{{menuItemTextRestFontWeight}}',
      menuItemTextRestFontStyle: '{{menuItemTextRestFontStyle}}',
      menuItemTextRestTextTransform: '{{menuItemTextRestTextTransform}}',
      menuItemTextRestLetterSpacing: '{{menuItemTextRestLetterSpacing}}',
      menuItemTextRestTextShadow: '{{menuItemTextRestTextShadow}}',
      menuItemTextHoverColor: '{{menuItemTextHoverColor}}',
      menuItemTextHoverFontSize: '{{menuItemTextHoverFontSize}}',
      menuItemTextHoverFontWeight: '{{menuItemTextHoverFontWeight}}',
      menuItemTextHoverFontStyle: '{{menuItemTextHoverFontStyle}}',
      menuItemTextHoverTextTransform: '{{menuItemTextHoverTextTransform}}',
      menuItemTextHoverLetterSpacing: '{{menuItemTextHoverLetterSpacing}}',
      menuItemTextHoverTextShadow: '{{menuItemTextHoverTextShadow}}'
    }
  },
  {
    id: 'menu-item-description-fade',
    category: 'menuItemTextEffects',
    name: 'Description Fade',
    description: 'Fade description text on non-hovered items',
    cssTemplate: `
      .item__Content-wkeq8p-4 p {
        transition: opacity {{animationDuration}}s ease;
        opacity: calc(1 - 0.4 * {{descriptionFadeStrength}});
      }
      
      .item__DishComponent-wkeq8p-0.cursor:hover .item__Content-wkeq8p-4 p {
        opacity: 1;
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      descriptionFadeStrength: '{{descriptionFadeStrength}}'
    }
  }
];

// =============================================================================
// CATEGORY HEADERS
// =============================================================================

// Category Header Style Templates (Single Selection - Base styling)
export const categoryHeaderStyle: ComponentTemplate[] = [
  {
    id: 'category-underline',
    category: 'categoryHeaderStyle',
    name: 'Full-Width Underline',
    description: 'Category with full-width coloured underline that matches text length',
    cssTemplate: `
      /* Category wrapper uses flex layout */
      .components__DishGridWrapper-hw0num-5 .m-b-6 {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      
      /* Category headers with full-width underline */
      .indexExperimental__CategoryName-sc-1errik4-4 {
        position: relative;
        padding-bottom: 2px;
      }
      
      /* Full-width underline - only on category names, not descriptions */
      .indexExperimental__CategoryName-sc-1errik4-4:not(.italic):after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 8px; /* Stop before padding to match text width */
        height: 3px;
        background-color: {{categoryUnderlineColor}};
        border-radius: 2px;
      }
      
      /* Category descriptions - no underline */
      .indexExperimental__CategoryName-sc-1errik4-4.lhp.italic {
        font-size: 16px !important;
        font-weight: normal !important;
        color: #c0c0c0;
        text-transform: none;
        letter-spacing: normal;
        margin-top: 5px;
        display: block !important;
        width: 100%;
        position: relative;
      }
      
      /* Ensure descriptions never get underlines */
      .indexExperimental__CategoryName-sc-1errik4-4.lhp.italic:after {
        display: none !important;
      }
      
      @media (max-width: 768px) {
        .indexExperimental__CategoryName-sc-1errik4-4 {
          font-size: 24px !important;
        }
      }
    `,
    variables: {
      categoryUnderlineColor: '{{categoryUnderlineColor}}'
    }
  },
  {
    id: 'category-gradient-underline',
    category: 'categoryHeaderStyle',
    name: 'Gradient Underline',
    description: 'Category with full-width gradient underline',
    cssTemplate: `
      /* Category wrapper uses flex layout */
      .components__DishGridWrapper-hw0num-5 .m-b-6 {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      
      /* Category headers with gradient underline */
      .indexExperimental__CategoryName-sc-1errik4-4 {
        position: relative;
        padding-bottom: 2px;
      }
      
      /* Full-width gradient underline - only on category names */
      .indexExperimental__CategoryName-sc-1errik4-4:not(.italic):after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 8px; /* Stop before padding to match text width */
        height: 3px;
        background: linear-gradient(to right, {{categoryUnderlinePrimaryColor}}, {{categoryUnderlineSecondaryColor}});
        border-radius: 2px;
      }
      
      /* Category descriptions - no underline */
      .indexExperimental__CategoryName-sc-1errik4-4.lhp.italic {
        font-size: 16px !important;
        font-weight: normal !important;
        color: #c0c0c0;
        text-transform: none;
        letter-spacing: normal;
        margin-top: 5px;
        display: block !important;
        width: 100%;
        position: relative;
      }
      
      /* Ensure descriptions never get underlines */
      .indexExperimental__CategoryName-sc-1errik4-4.lhp.italic:after {
        display: none !important;
      }
      
      @media (max-width: 768px) {
        .indexExperimental__CategoryName-sc-1errik4-4 {
          font-size: 24px !important;
        }
      }
    `,
    variables: {
      categoryUnderlinePrimaryColor: '{{categoryUnderlinePrimaryColor}}',
      categoryUnderlineSecondaryColor: '{{categoryUnderlineSecondaryColor}}'
    }
  },
  {
    id: 'category-animated-underline',
    category: 'categoryHeaderStyle',
    name: 'Animated Underline',
    description: 'Category with full-width animated gradient underline',
    cssTemplate: `
      /* Category wrapper uses flex layout */
      .components__DishGridWrapper-hw0num-5 .m-b-6 {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      
      /* Category headers with animated underline */
      .indexExperimental__CategoryName-sc-1errik4-4 {
        position: relative;
        padding-bottom: 2px;
      }
      
      /* Full-width animated underline - only on category names */
      .indexExperimental__CategoryName-sc-1errik4-4:not(.italic):after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 8px; /* Stop before padding to match text width */
        height: 4px;
        background: linear-gradient(to right, {{categoryUnderlinePrimaryColor}} 0%, {{categoryUnderlineSecondaryColor}} 50%, {{categoryUnderlinePrimaryColor}} 100%);
        border-radius: 2px;
        background-size: 200% 100%;
        animation: rainbowSlide 3s linear infinite;
      }
      
      @keyframes rainbowSlide {
        0% { background-position: 0% 50%; }
        100% { background-position: 200% 50%; }
      }
      
      /* Category descriptions - no underline */
      .indexExperimental__CategoryName-sc-1errik4-4.lhp.italic {
        font-size: 16px !important;
        font-weight: normal !important;
        color: #c0c0c0;
        text-transform: none;
        letter-spacing: normal;
        margin-top: 5px;
        display: block !important;
        width: 100%;
        position: relative;
      }
      
      /* Ensure descriptions never get underlines */
      .indexExperimental__CategoryName-sc-1errik4-4.lhp.italic:after {
        display: none !important;
      }
      
      @media (max-width: 768px) {
        .indexExperimental__CategoryName-sc-1errik4-4 {
          font-size: 24px !important;
        }
      }
    `,
    variables: {
      categoryUnderlinePrimaryColor: '{{categoryUnderlinePrimaryColor}}',
      categoryUnderlineSecondaryColor: '{{categoryUnderlineSecondaryColor}}'
    }
  }
];

// Category Header Text Effects Templates (Multi-Selection - Text styling that can combine with underlines)
export const categoryHeaderTextEffects: ComponentTemplate[] = [
  {
    id: 'category-base-styling',
    category: 'categoryHeaderTextEffects',
    name: 'Base Styling',
    description: 'Essential typography and layout for category headers',
    cssTemplate: `
      .indexExperimental__CategoryName-sc-1errik4-4 {
        display: inline-block;
        margin-bottom: 20px;
        font-weight: {{fontWeight}};
        font-size: {{fontSize}}px !important;
        font-style: {{fontStyle}};
        letter-spacing: 1px;
        text-transform: uppercase;
        padding-right: 8px;
        overflow: visible;
      }
      
      /* Category descriptions styling */
      .indexExperimental__CategoryName-sc-1errik4-4.lhp.italic {
        font-size: 16px !important;
        font-weight: normal !important;
        color: #c0c0c0;
        text-transform: none;
        letter-spacing: normal;
        margin-top: 5px;
        display: block !important;
        width: 100%;
        font-style: normal;
        background: none !important;
        -webkit-background-clip: initial !important;
        -webkit-text-fill-color: initial !important;
        background-clip: initial !important;
      }
      
      @media (max-width: 768px) {
        .indexExperimental__CategoryName-sc-1errik4-4 {
          font-size: calc({{fontSize}}px * 0.85) !important;
        }
      }
    `,
    variables: {
      fontWeight: '{{fontWeight}}',
      fontSize: '{{fontSize}}',
      fontStyle: '{{fontStyle}}'
    }
  },
  {
    id: 'category-text-color',
    category: 'categoryHeaderTextEffects',
    name: 'Solid Colour',
    description: 'Apply solid colour to category headers',
    cssTemplate: `
      .indexExperimental__CategoryName-sc-1errik4-4 {
        color: {{categoryHeaderTextColor}} !important;
        background: none !important;
        -webkit-background-clip: initial !important;
        -webkit-text-fill-color: initial !important;
        background-clip: initial !important;
      }
    `,
    variables: {
      categoryHeaderTextColor: '{{categoryHeaderTextColor}}'
    }
  },
  {
    id: 'category-gradient-text',
    category: 'categoryHeaderTextEffects',
    name: 'Gradient Color',
    description: 'Apply gradient color to category headers',
    cssTemplate: `
      .indexExperimental__CategoryName-sc-1errik4-4 {
        background: linear-gradient(135deg, {{categoryHeaderGradientPrimary}} 0%, {{categoryHeaderGradientSecondary}} 100%) !important;
        -webkit-background-clip: text !important;
        -webkit-text-fill-color: transparent !important;
        background-clip: text !important;
        color: transparent !important;
      }
      
      /* Fallback for browsers that don't support background-clip: text */
      @supports not (-webkit-background-clip: text) {
        .indexExperimental__CategoryName-sc-1errik4-4 {
          color: {{categoryHeaderTextColor}} !important;
          background: none !important;
        }
      }
    `,
    variables: {
      categoryHeaderGradientPrimary: '{{categoryHeaderGradientPrimary}}',
      categoryHeaderGradientSecondary: '{{categoryHeaderGradientSecondary}}',
      categoryHeaderTextColor: '{{categoryHeaderTextColor}}'
    }
  }
];

// Navigation Style Templates
export const navigationStyles: ComponentTemplate[] = [
  {
    id: 'nav-customizable',
    category: 'navigationStyles',
    name: 'Customizable Navigation',
    description: 'Full navigation styling with customisable colours',
    cssTemplate: `
      /* Top navigation bar styling */
      .nav__Wrapper-sc-1e8blr1-0 {
        background: {{navMainBg}} !important;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
        border-bottom: 1px solid {{navMainBorder}};
      }
      
      /* Top bar (location/service type) styling */
      .top__Wrapper-sc-1amtn2l-0 {
        background: {{navTopBg}} !important;
        backdrop-filter: blur(5px);
        border-bottom: 1px solid {{navTopBorder}};
      }
      
      /* Top bar text colors */
      .top__Content-sc-1amtn2l-1 p,
      .top__LabelText-sc-1amtn2l-5 {
        color: {{navTopText}} !important;
      }
      
      /* Top bar select dropdown styling */
      .top__SelectInput-sc-1amtn2l-3 {
        color: {{navTopText}} !important;
      }
      
      /* Dropdown arrow and separator colors */
      .css-tlfecz-indicatorContainer svg {
        color: {{navTopText}} !important;
      }
      
      .css-1okebmr-indicatorSeparator {
        background-color: {{navTopText}} !important;
        opacity: 0.2;
      }
      
      /* Bottom navigation (mobile) styling */
      .nav-bottom__Wrapper-sc-10awnlt-0 {
        background: {{navBottomBg}} !important;
        border-top: 1px solid {{navBottomBorder}};
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }
      
      /* Logo styling */
      .main__Logo-n31oe-2 span {
        font-weight: 700 !important;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: {{navLogoText}} !important;
      }
      
      /* Side menu/slider wrapper */
      .slider__MenuWrapper-sc-1bsd7ul-0,
      .menu__Wrapper-sc-1bsd7ul-0 {
        background: {{navSliderBg}} !important;
        backdrop-filter: blur(15px);
        -webkit-backdrop-filter: blur(15px);
      }
      
      /* Menu item text in slider */
      .slider__MenuWrapper-sc-1bsd7ul-0 a,
      .menu__Wrapper-sc-1bsd7ul-0 a {
        color: {{navSliderText}} !important;
      }
      
      /* Navigation components - using flexible class matching */
      [class*="components__TabSelectComponent"] {
        background: {{navSliderBg}} !important;
      }
      
      [class*="MenuSearchButton__SearchButton"] {
        background: {{navSearchButtonBg}} !important;
        color: {{navSearchButtonText}} !important;
      }
      
      /* Category nav tabs in slider menu */
      [class*="category-nav-bar__Tabs"] {
        background: {{navSliderBg}} !important;
      }
    `,
    variables: {
      navMainBg: '{{navMainBg}}',
      navMainBorder: '{{navMainBorder}}',
      navTopBg: '{{navTopBg}}',
      navTopBorder: '{{navTopBorder}}',
      navTopText: '{{navTopText}}',
      navBottomBg: '{{navBottomBg}}',
      navBottomBorder: '{{navBottomBorder}}',
      navLogoText: '{{navLogoText}}',
      navSliderBg: '{{navSliderBg}}',
      navSliderText: '{{navSliderText}}',
      navSearchButtonBg: '{{navSearchButtonBg}}',
      navSearchButtonText: '{{navSearchButtonText}}'
    }
  }
];

// Button Effect Templates
export const buttonEffects: ComponentTemplate[] = [
  {
    id: 'button-ripple',
    category: 'buttonEffects',
    name: 'Enhanced Ripple Effect',
    description: 'Professional ripple animation with proper event handling and cleanup',
    cssTemplate: `
      .sc-bdVaJa, button[color="primary"], .button, [role="button"] {
        transition: all 0.5s ease;
        border-radius: 6px;
        box-shadow: 0 4px 10px rgba({{primaryColorRgb}}, 0.2);
        letter-spacing: 0.5px;
        text-transform: uppercase;
        font-weight: 700;
        position: relative;
        overflow: hidden;
      }
      
      .sc-bdVaJa:hover, button[color="primary"]:hover, .button:hover, [role="button"]:hover {
        box-shadow: 0 6px 15px rgba({{primaryColorRgb}}, 0.4);
        transform: translateY(-2px);
      }
    `,
    jsTemplate: `
      // Enhanced ripple effect with proper cleanup
      (function() {
        // Add ripple CSS if not already present
        if (!document.getElementById('ripple-styles')) {
          const style = document.createElement('style');
          style.id = 'ripple-styles';
          style.textContent = \`
            .ripple {
              position: absolute;
              background-color: rgba(255, 255, 255, 0.4);
              border-radius: 50%;
              transform: scale(0);
              animation: ripple 0.6s linear;
              pointer-events: none;
            }
            
            @keyframes ripple {
              to {
                transform: scale(4);
                opacity: 0;
              }
            }
          \`;
          document.head.appendChild(style);
        }
        
        function createRipple(event) {
          const button = event.currentTarget;
          
          // Remove any existing ripples
          const existingRipples = button.querySelectorAll('.ripple');
          existingRipples.forEach(ripple => ripple.remove());
          
          const circle = document.createElement("span");
          const diameter = Math.max(button.clientWidth, button.clientHeight);
          const radius = diameter / 2;
          const rect = button.getBoundingClientRect();
          
          circle.style.width = circle.style.height = diameter + "px";
          circle.style.left = (event.clientX - rect.left - radius) + "px";
          circle.style.top = (event.clientY - rect.top - radius) + "px";
          circle.classList.add("ripple");
          
          button.appendChild(circle);
          
          // Clean up after animation
          setTimeout(() => {
            if (circle.parentNode) {
              circle.remove();
            }
          }, 600);
        }
        
        // Apply to all buttons with better selectors
        function attachRippleEffect() {
          const buttons = document.querySelectorAll('.sc-bdVaJa:not([data-ripple]), button[color="primary"]:not([data-ripple]), .button:not([data-ripple]), [role="button"]:not([data-ripple])');
          buttons.forEach(button => {
            button.setAttribute('data-ripple', 'true');
            button.addEventListener('click', createRipple);
          });
        }
        
        // Initial attachment
        attachRippleEffect();
        
        // Reattach when new buttons are added
        const observer = new MutationObserver(() => {
          attachRippleEffect();
        });
        
        observer.observe(document.body, { childList: true, subtree: true });
      })();
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      movementStrength: '{{movementStrength}}'
    }
  },
  {
    id: 'button-glow',
    category: 'buttonEffects',
    name: 'Enhanced Glow Effect',
    description: 'Buttons with enhanced glowing animation',
    cssTemplate: `
      .sc-bdVaJa, button[color="primary"], .button, [role="button"] {
        transition: all 0.5s ease;
        border-radius: 6px;
        box-shadow: 0 4px 10px rgba({{primaryColorRgb}}, 0.2);
        letter-spacing: 0.5px;
        text-transform: uppercase;
        font-weight: 700;
        position: relative;
        overflow: hidden;
      }
      
      .sc-bdVaJa:hover, button[color="primary"]:hover, .button:hover, [role="button"]:hover {
        box-shadow: 0 6px 15px rgba({{primaryColorRgb}}, 0.4);
        transform: translateY(-2px);
      }
      
      #order-button, .order-now-button {
        animation: enhancedButtonGlow 2s infinite;
      }
      
      @keyframes enhancedButtonGlow {
        0% { 
          box-shadow: 0 4px 10px rgba({{primaryColorRgb}}, 0.2), 0 0 0 0 rgba({{primaryColorRgb}}, 0.4); 
        }
        70% { 
          box-shadow: 0 6px 15px rgba({{primaryColorRgb}}, 0.4), 0 0 0 10px rgba({{primaryColorRgb}}, 0); 
        }
        100% { 
          box-shadow: 0 4px 10px rgba({{primaryColorRgb}}, 0.2), 0 0 0 0 rgba({{primaryColorRgb}}, 0); 
        }
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      movementStrength: '{{movementStrength}}'
    }
  }
];

// Service Choice Style Templates
export const serviceChoiceStyles: ComponentTemplate[] = [
  {
    id: 'service-enhanced',
    category: 'serviceChoiceStyles',
    name: 'Enhanced Service Choice',
    description: 'Improved delivery/pickup selection',
    cssTemplate: `
      .components__ChoiceComponent-sc-1hvhesa-1 {
        border: 1px solid rgba({{primaryColorRgb}}, 0.1);
        transition: all 0.3s ease;
        border-radius: 8px;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
      }
      
      .components__ChoiceComponent-sc-1hvhesa-1:hover {
        background: rgba({{primaryColorRgb}}, 0.1);
        transform: translateY(-3px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2), 0 0 15px rgba({{primaryColorRgb}}, 0.3);
      }
      
      .components__ChoiceIcon-sc-1hvhesa-2 {
        transition: all 0.3s ease;
      }
      
      .components__ChoiceComponent-sc-1hvhesa-1:hover .components__ChoiceIcon-sc-1hvhesa-2 {
        color: {{primaryColor}};
        transform: scale(1.1);
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColor: '{{primaryColor}}',
      primaryColorRgb: '{{primaryColorRgb}}'
    }
  },
  {
    id: 'service-comprehensive',
    category: 'serviceChoiceStyles',
    name: 'Comprehensive Service Choice',
    description: 'Premium styling for delivery/pickup selection with modal enhancements',
    cssTemplate: `
      /* Enhanced Service Choice Components */
      .components__ChoiceComponent-sc-1hvhesa-1 {
        border: 1px solid rgba({{primaryColorRgb}}, 0.1);
        transition: all 0.3s ease;
        border-radius: 8px;
        box-shadow: {{comprehensiveServiceShadow}};
        background: {{comprehensiveServiceBackground}};
      }
      
      .components__ChoiceComponent-sc-1hvhesa-1:hover {
        background: {{comprehensiveServiceHoverBackground}};
        transform: translateY(-3px);
        box-shadow: {{comprehensiveServiceHoverShadow}};
        border-color: rgba({{primaryColorRgb}}, 0.3);
      }
      
      .components__ChoiceIcon-sc-1hvhesa-2 {
        transition: all 0.3s ease;
        color: {{comprehensiveServiceIconColor}};
      }
      
      .components__ChoiceComponent-sc-1hvhesa-1:hover .components__ChoiceIcon-sc-1hvhesa-2 {
        color: {{primaryColor}};
        transform: scale(1.1) rotate(5deg);
      }
      
      /* Modal styling enhancements */
      .sc-eNQAEJ.eUuYTz, .sc-hMqMXs.eUuYTz {
        border: 1px solid rgba({{primaryColorRgb}}, 0.1);
        box-shadow: {{comprehensiveModalShadow}};
        background: {{comprehensiveModalBackground}};
        overflow: hidden;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }
      
      /* Service info headers */
      .services__ServiceInfoHeader-a2rvuw-1 {
        transition: all 0.3s ease;
        padding: 15px 18px !important;
        border-radius: 8px;
        background-color: {{comprehensiveServiceHeaderBackground}};
        border: 1px solid rgba({{primaryColorRgb}}, 0.15);
      }
      
      .services__ServiceInfoHeader-a2rvuw-1:hover {
        background-color: {{comprehensiveServiceHeaderHoverBackground}};
        transform: translateY(-3px);
        border-color: rgba({{primaryColorRgb}}, 0.3);
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColor: '{{primaryColor}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      comprehensiveServiceBackground: '{{comprehensiveServiceBackground}}',
      comprehensiveServiceHoverBackground: '{{comprehensiveServiceHoverBackground}}',
      comprehensiveServiceShadow: '{{comprehensiveServiceShadow}}',
      comprehensiveServiceHoverShadow: '{{comprehensiveServiceHoverShadow}}',
      comprehensiveServiceIconColor: '{{comprehensiveServiceIconColor}}',
      comprehensiveModalBackground: '{{comprehensiveModalBackground}}',
      comprehensiveModalShadow: '{{comprehensiveModalShadow}}',
      comprehensiveServiceHeaderBackground: '{{comprehensiveServiceHeaderBackground}}',
      comprehensiveServiceHeaderHoverBackground: '{{comprehensiveServiceHeaderHoverBackground}}'
    }
  },
  {
    id: 'service-premium',
    category: 'serviceChoiceStyles',
    name: 'Premium Service Choice',
    description: 'Luxury styling with brand colour integration and advanced hover effects',
    cssTemplate: `
      /* Premium Service Choice Components */
      .components__ChoiceComponent-sc-1hvhesa-1 {
        border: 2px solid rgba({{primaryColorRgb}}, 0.2);
        transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
        border-radius: 12px;
        box-shadow: {{serviceChoiceShadow}};
        background: {{serviceChoiceBackground}};
        position: relative;
        overflow: hidden;
      }
      
      .components__ChoiceComponent-sc-1hvhesa-1::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba({{primaryColorRgb}}, 0.1), transparent);
        transition: left 0.6s ease;
      }
      
      .components__ChoiceComponent-sc-1hvhesa-1:hover::before {
        left: 100%;
      }
      
      .components__ChoiceComponent-sc-1hvhesa-1:hover {
        background: {{serviceChoiceHoverBackground}} !important;
        transform: translateY(-5px) scale(1.02);
        box-shadow: {{serviceChoiceHoverShadow}};
        border-color: rgba({{primaryColorRgb}}, 0.5);
      }
      
      .components__ChoiceIcon-sc-1hvhesa-2 {
        transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
        color: {{serviceChoiceIconColor}};
        position: relative;
        z-index: 1;
      }
      
      .components__ChoiceComponent-sc-1hvhesa-1:hover .components__ChoiceIcon-sc-1hvhesa-2 {
        color: {{primaryColor}};
        transform: scale(1.2) rotate(10deg);
        filter: drop-shadow(0 0 10px rgba({{primaryColorRgb}}, 0.6));
      }
      
      /* Premium modal styling */
      .sc-eNQAEJ.eUuYTz, .sc-hMqMXs.eUuYTz {
        border: 2px solid rgba({{primaryColorRgb}}, 0.2);
        box-shadow: {{modalShadow}};
        background: {{modalBackground}};
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
        border-radius: 16px;
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColor: '{{primaryColor}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      secondaryColor: '{{secondaryColor}}',
      secondaryColorRgb: '{{secondaryColorRgb}}',
      serviceChoiceBackground: '{{serviceChoiceBackground}}',
      serviceChoiceHoverBackground: '{{serviceChoiceHoverBackground}}',
      serviceChoiceShadow: '{{serviceChoiceShadow}}',
      serviceChoiceHoverShadow: '{{serviceChoiceHoverShadow}}',
      serviceChoiceIconColor: '{{serviceChoiceIconColor}}',
      modalBackground: '{{modalBackground}}',
      modalShadow: '{{modalShadow}}'
    }
  }
];


// Footer Enhancement Templates
export const footerEnhancements: ComponentTemplate[] = [
  {
    id: 'footer-line',
    category: 'footerEnhancements',
    name: 'Gradient Line',
    description: 'Subtle gradient line above footer',
    cssTemplate: `
      .components__Wrapper-sc-1vb6jo6-0 {
        position: relative;
        overflow: hidden;
      }
      
      .components__Wrapper-sc-1vb6jo6-0:before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 1px;
        background: linear-gradient(to right, transparent, rgba({{primaryColorRgb}}, 0.5), transparent);
      }
    `,
    variables: {
      primaryColorRgb: '{{primaryColorRgb}}'
    }
  }
];

// Loading Animation Templates
export const loadingAnimations: ComponentTemplate[] = [
  {
    id: 'fade-in-stagger',
    category: 'loadingAnimations',
    name: 'Staggered Fade In',
    description: 'Menu items fade in with staggered timing',
    jsTemplate: `
      // Staggered fade-in animation for menu items
      document.addEventListener('DOMContentLoaded', function() {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate__animated', 'animate__fadeIn');
              observer.unobserve(entry.target);
            }
          });
        }, {
          root: null,
          threshold: 0.1
        });
        
        // Apply to menu categories
        document.querySelectorAll('.components__DishGridWrapper-hw0num-5').forEach(item => {
          observer.observe(item);
        });
        
        // Apply staggered animation to menu items
        document.querySelectorAll('.item__DishComponent-wkeq8p-0').forEach((item, index) => {
          item.style.opacity = '0';
          item.style.transform = 'translateY(20px)';
          item.style.transition = 'opacity {{animationDuration}}s ease, transform {{animationDuration}}s ease';
          item.style.transitionDelay = index * 0.05 + 's';
          
          setTimeout(() => {
            item.style.opacity = '1';
            item.style.transform = 'translateY(0)';
          }, 100 + (index * 50));
        });
      });
    `,
    variables: {
      animationDuration: '{{animationDuration}}'
    }
  },
  {
    id: 'comprehensive-loading',
    category: 'loadingAnimations',
    name: 'Comprehensive Loading Animations',
    description: 'Professional loading animations with intersection observer and proper cleanup',
    jsTemplate: `
      // Comprehensive loading animations with proper cleanup
      document.addEventListener('DOMContentLoaded', function() {
        // Intersection Observer for scroll animations
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate__animated', 'animate__fadeIn');
              observer.unobserve(entry.target);
            }
          });
        }, {
          root: null,
          threshold: 0.1
        });
        
        // Apply to menu categories
        document.querySelectorAll('.components__DishGridWrapper-hw0num-5').forEach(item => {
          observer.observe(item);
        });
        
        // Special effects for category names
        document.querySelectorAll('.indexExperimental__CategoryName-sc-1errik4-4').forEach((item, index) => {
          // Skip category descriptions (italic class)
          if (!item.classList.contains('italic')) {
            item.classList.add('animate__animated', 'animate__fadeInLeft');
            item.style.animationDuration = '0.8s';
            item.style.animationDelay = \`\${index * 0.1}s\`;
          }
        });
        
        // Enhanced staggered fade-in for menu items with proper cleanup
        document.querySelectorAll('.col').forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          // Only animate items that are initially visible on page load
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            // Set initial state
            item.style.opacity = '0';
            item.style.transform = 'translateY(30px) scale(0.95)';
            item.style.transition = 'opacity {{animationDuration}}s cubic-bezier(0.25, 0.8, 0.25, 1), transform {{animationDuration}}s cubic-bezier(0.25, 0.8, 0.25, 1)';
            item.style.transitionDelay = \`\${index * 0.05}s\`;
            
            // Animate in with bounce
            setTimeout(() => {
              item.style.opacity = '1';
              item.style.transform = 'translateY(0) scale(1)';
              
              // CRITICAL: Clear transition delay after animation completes
              // This ensures hover effects respond immediately
              setTimeout(() => {
                item.style.transitionDelay = '0s';
              }, {{animationDuration}} * 1000);
            }, 100 + (index * 50));
          }
        });
        
        // Logo hover effect
        const logo = document.querySelector('#header-logo-image, .main__Logo-n31oe-2 img');
        if (logo) {
          logo.style.transition = 'all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)';
          logo.addEventListener('mouseover', function() {
            this.style.transform = 'scale(1.05)';
            this.style.filter = 'drop-shadow(0 0 15px rgba({{primaryColorRgb}}, 0.6))';
          });
          logo.addEventListener('mouseout', function() {
            this.style.transform = 'scale(1)';
            this.style.filter = 'none';
          });
        }
        
        // Enhanced tag animations
        document.querySelectorAll('.sc-kvZOFW').forEach(tag => {
          tag.style.transition = 'all 0.2s ease';
          tag.addEventListener('mouseover', function() {
            this.style.transform = 'translateY(-2px) scale(1.05)';
          });
          tag.addEventListener('mouseout', function() {
            this.style.transform = 'translateY(0) scale(1)';
          });
        });
      });
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      movementStrength: '{{movementStrength}}'
    }
  },
  {
    id: 'scroll-reveal-animations',
    category: 'loadingAnimations',
    name: 'Scroll Reveal Animations',
    description: 'Advanced scroll-based animations with intersection observer',
    jsTemplate: `
      // Advanced scroll reveal animations
      document.addEventListener('DOMContentLoaded', function() {
        // Create intersection observer for scroll reveals
        const scrollObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const target = entry.target;
              target.classList.add('scroll-revealed');
              
              // Add specific animations based on element type
              if (target.classList.contains('item__DishComponent-wkeq8p-0')) {
                target.style.opacity = '1';
                target.style.transform = 'translateY(0) scale(1)';
              } else if (target.classList.contains('indexExperimental__CategoryName-sc-1errik4-4')) {
                target.classList.add('animate__animated', 'animate__fadeInLeft');
              }
              
              scrollObserver.unobserve(target);
            }
          });
        }, {
          root: null,
          rootMargin: '0px 0px -50px 0px',
          threshold: 0.1
        });
        
        // Set up initial states and observe elements
        document.querySelectorAll('.item__DishComponent-wkeq8p-0').forEach((item, index) => {
          item.style.opacity = '0';
          item.style.transform = 'translateY(50px) scale(0.9)';
          item.style.transition = 'all {{animationDuration}}s cubic-bezier(0.25, 0.8, 0.25, 1)';
          item.style.transitionDelay = \`\${(index % 6) * 0.1}s\`;
          scrollObserver.observe(item);
        });
        
        // Observe category headers
        document.querySelectorAll('.indexExperimental__CategoryName-sc-1errik4-4:not(.italic)').forEach(header => {
          scrollObserver.observe(header);
        });
        
        // Add CSS for revealed elements
        const style = document.createElement('style');
        style.textContent = \`
          .scroll-revealed {
            animation-fill-mode: forwards;
          }
          
          .scroll-revealed.item__DishComponent-wkeq8p-0 {
            opacity: 1 !important;
            transform: translateY(0) scale(1) !important;
          }
        \`;
        document.head.appendChild(style);
        
        // Clear transition delays after animations complete
        setTimeout(() => {
          document.querySelectorAll('.item__DishComponent-wkeq8p-0').forEach(item => {
            item.style.transitionDelay = '0s';
          });
        }, 2000);
      });
    `,
    variables: {
      animationDuration: '{{animationDuration}}'
    }
  },
  {
    id: 'interactive-elements',
    category: 'loadingAnimations',
    name: 'Interactive Element Animations',
    description: 'Advanced interactive animations for buttons, search, and UI elements',
    jsTemplate: `
      // Interactive element animations
      document.addEventListener('DOMContentLoaded', function() {
        // Enhanced button ripple effect
        function createRipple(event) {
          const button = event.currentTarget;
          const circle = document.createElement("span");
          const diameter = Math.max(button.clientWidth, button.clientHeight);
          const radius = diameter / 2;
          
          circle.style.width = circle.style.height = \`\${diameter}px\`;
          circle.style.left = \`\${event.clientX - button.getBoundingClientRect().left - radius}px\`;
          circle.style.top = \`\${event.clientY - button.getBoundingClientRect().top - radius}px\`;
          circle.classList.add("ripple");
          
          const ripple = button.getElementsByClassName("ripple")[0];
          if (ripple) ripple.remove();
          
          button.appendChild(circle);
        }
        
        // Add enhanced ripple styles
        if (!document.getElementById('enhanced-ripple-styles')) {
          const style = document.createElement('style');
          style.id = 'enhanced-ripple-styles';
          style.textContent = \`
            .ripple {
              position: absolute;
              background: linear-gradient(45deg, rgba({{primaryColorRgb}}, 0.6), rgba(255, 255, 255, 0.4));
              border-radius: 50%;
              transform: scale(0);
              animation: enhancedRipple 0.6s ease-out;
              pointer-events: none;
            }
            
            @keyframes enhancedRipple {
              to {
                transform: scale(4);
                opacity: 0;
              }
            }
            
            .sc-bdVaJa, button[color="primary"] {
              position: relative;
              overflow: hidden;
            }
          \`;
          document.head.appendChild(style);
        }
        
        // Apply ripple to buttons
        document.querySelectorAll('.sc-bdVaJa, button[color="primary"]').forEach(button => {
          button.addEventListener('click', createRipple);
        });
        
        // Search button animation
        const searchButton = document.querySelector('.MenuSearchButton__SearchButton-sc-1ph0p6o-0');
        if (searchButton) {
          searchButton.addEventListener('mouseenter', function() {
            this.style.transform = 'rotate(360deg) scale(1.2)';
            this.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)';
          });
          searchButton.addEventListener('mouseleave', function() {
            this.style.transform = 'rotate(0deg) scale(1)';
          });
        }
        
        // Enhanced order button effects
        const orderButton = document.getElementById('order-button');
        if (orderButton) {
          orderButton.addEventListener('mouseenter', function() {
            this.style.boxShadow = '0 6px 20px rgba({{primaryColorRgb}}, 0.4), 0 0 40px rgba({{primaryColorRgb}}, 0.3)';
            this.style.transform = 'translateY(-2px) scale(1.02)';
          });
          
          orderButton.addEventListener('mouseleave', function() {
            this.style.boxShadow = '';
            this.style.transform = '';
          });
        }
        
        // Navigation item hover effects
        document.querySelectorAll('.sc-fjdhpX.czAJVa').forEach(item => {
          item.style.transition = 'all 0.3s ease';
          item.addEventListener('mouseenter', function() {
            this.style.color = '{{primaryColor}}';
            this.style.transform = 'translateY(-2px)';
          });
          item.addEventListener('mouseleave', function() {
            this.style.color = '';
            this.style.transform = 'translateY(0)';
          });
        });
        
        // Social icon animations
        document.querySelectorAll('.components__SocialIcons-sc-1vb6jo6-3 a').forEach(icon => {
          icon.style.transition = 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)';
          icon.addEventListener('mouseenter', function() {
            this.style.transform = 'translateY(-3px) scale(1.1)';
            this.style.color = '{{primaryColor}}';
          });
          icon.addEventListener('mouseleave', function() {
            this.style.transform = 'translateY(0) scale(1)';
            this.style.color = '';
          });
        });
      });
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColor: '{{primaryColor}}',
      primaryColorRgb: '{{primaryColorRgb}}'
    }
  }
];

// Effects Enhancement Templates - These can be combined as they affect different CSS properties
export const effectsEnhancements: ComponentTemplate[] = [
  {
    id: 'shadow-depth',
    category: 'effectsEnhancements',
    name: 'Shadow Depth',
    description: 'Adds enhanced shadow depth to elements',
    cssTemplate: `
      .item__DishComponent-wkeq8p-0 {
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08) !important;
      }
      
      .item__DishComponent-wkeq8p-0:hover {
        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15), 0 5px 15px rgba(0, 0, 0, 0.1) !important;
      }
    `,
    variables: {},
    stackable: true
  },
  {
    id: 'border-glow',
    category: 'effectsEnhancements',
    name: 'Border Glow',
    description: 'Adds a subtle glow around elements',
    cssTemplate: `
      .item__DishComponent-wkeq8p-0 {
        border: 1px solid rgba({{primaryColorRgb}}, 0.2);
      }
      
      .item__DishComponent-wkeq8p-0:hover {
        border-color: rgba({{primaryColorRgb}}, 0.5);
        box-shadow: 0 0 15px rgba({{primaryColorRgb}}, 0.3);
      }
    `,
    variables: {
      primaryColorRgb: '{{primaryColorRgb}}'
    },
    stackable: true
  },
  {
    id: 'gradient-overlay',
    category: 'effectsEnhancements',
    name: 'Gradient Overlay',
    description: 'Adds a subtle gradient overlay effect',
    cssTemplate: `
      .item__DishComponent-wkeq8p-0::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba({{primaryColorRgb}}, 0.05) 0%, rgba({{secondaryColorRgb}}, 0.05) 100%);
        opacity: 0;
        transition: opacity {{animationDuration}}s ease;
        pointer-events: none;
        z-index: 1;
      }
      
      .item__DishComponent-wkeq8p-0:hover::before {
        opacity: 1;
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      secondaryColorRgb: '{{secondaryColorRgb}}'
    },
    stackable: true
  },
  {
    id: 'text-enhancement',
    category: 'effectsEnhancements',
    name: 'Text Enhancement',
    description: 'Enhances text readability and styling',
    cssTemplate: `
      .item__DishComponent-wkeq8p-0 h3,
      .item__DishComponent-wkeq8p-0 .item-title {
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        font-weight: 600;
        letter-spacing: 0.3px;
      }
      
      .item__DishComponent-wkeq8p-0 p,
      .item__DishComponent-wkeq8p-0 .item-description {
        line-height: 1.6;
        color: rgba(0, 0, 0, 0.7);
      }
    `,
    variables: {},
    stackable: true
  }
];

// Animation Combination Templates - These work together to create complex animations
export const animationsCombination: ComponentTemplate[] = [
  {
    id: 'stagger-fade-in',
    category: 'animationsCombination',
    name: 'Staggered Fade In',
    description: 'Elements fade in with staggered timing',
    jsTemplate: `
      // Staggered fade-in animation
      const elements = document.querySelectorAll('.item__DishComponent-wkeq8p-0');
      elements.forEach((element, index) => {
        element.style.opacity = '0';
        element.style.transform = 'translateY(20px)';
        element.style.transition = 'opacity {{animationDuration}}s ease, transform {{animationDuration}}s ease';
        
        setTimeout(() => {
          element.style.opacity = '1';
          element.style.transform = 'translateY(0)';
        }, index * 100);
      });
    `,
    variables: {
      animationDuration: '{{animationDuration}}'
    },
    stackable: true
  },
  {
    id: 'pulse-highlight',
    category: 'animationsCombination',
    name: 'Pulse Highlight',
    description: 'Subtle pulsing effect on new items',
    cssTemplate: `
      .item__DishComponent-wkeq8p-0.new-item {
        animation: pulseHighlight 3s ease-in-out;
      }
      
      @keyframes pulseHighlight {
        0% { background-color: rgba({{primaryColorRgb}}, 0); }
        50% { background-color: rgba({{primaryColorRgb}}, 0.1); }
        100% { background-color: rgba({{primaryColorRgb}}, 0); }
      }
    `,
    variables: {
      primaryColorRgb: '{{primaryColorRgb}}'
    },
    stackable: true
  },
  {
    id: 'scroll-reveal',
    category: 'animationsCombination',
    name: 'Scroll Reveal',
    description: 'Elements reveal as they come into view',
    jsTemplate: `
      // Intersection Observer for scroll reveal
      const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      }, observerOptions);
      
      document.querySelectorAll('.item__DishComponent-wkeq8p-0').forEach(item => {
        item.style.opacity = '0';
        item.style.transform = 'translateY(30px)';
        item.style.transition = 'opacity {{animationDuration}}s ease, transform {{animationDuration}}s ease';
        observer.observe(item);
      });
    `,
    cssTemplate: `
      .item__DishComponent-wkeq8p-0.revealed {
        opacity: 1 !important;
        transform: translateY(0) !important;
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}'
    },
    stackable: true
  }
];

// =============================================================================
// ADDITIONAL GRANULAR CATEGORIES
// =============================================================================

// Category Header Animation Templates (Multi-Selection - Animated header effects)
export const categoryHeaderAnimation: ComponentTemplate[] = [
  {
    id: 'category-header-fade-in',
    category: 'categoryHeaderAnimation',
    name: 'Fade In Animation',
    description: 'Smooth fade-in effect for category headers',
    cssTemplate: `
      .menu__CategoryComponent-sc-13vj7bl-0 {
        opacity: 0;
        animation: categoryFadeIn {{animationDuration}}s ease forwards;
      }
      
      @keyframes categoryFadeIn {
        to { opacity: 1; }
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}'
    },
    stackable: true
  }
];

// Button Hover Effect Templates (Multi-Selection - Button hover animations)
export const buttonHoverEffect: ComponentTemplate[] = [
  {
    id: 'button-hover-scale',
    category: 'buttonHoverEffect',
    name: 'Scale on Hover',
    description: 'Subtle scale effect on button hover',
    cssTemplate: `
      /* Single button mode (reservations disabled) */
      .sc-bdVaJa, 
      button[color="primary"],
      button.main__OrderButton-n31oe-8 {
        transition: all 0.3s ease;
        position: relative;
        text-transform: {{buttonRestTextTransform}} !important;
        {{buttonShadowRest}}
      }
      
      .sc-bdVaJa:hover, 
      button[color="primary"]:hover,
      button.main__OrderButton-n31oe-8:hover {
        transform: scale(1.05);
        text-transform: {{buttonHoverTextTransform}} !important;
        {{buttonShadowHover}}
      }
      
      /* Split button mode (reservations enabled) */
      .main__OrderButtonLeft-n31oe-9,
      .main__OrderButtonRight-n31oe-10,
      button.main__OrderButtonLeft-n31oe-9,
      button.main__OrderButtonRight-n31oe-10 {
        transition: all 0.3s ease !important;
        position: relative !important;
        text-transform: {{buttonRestTextTransform}} !important;
        margin: 0 !important;
      }
      
      /* Book button (left) - rounded left corners */
      .main__OrderButtonLeft-n31oe-9,
      button.main__OrderButtonLeft-n31oe-9 {
        border-radius: 8px 0 0 8px !important;
      }
      
      /* Order button (right) - rounded right corners */
      .main__OrderButtonRight-n31oe-10,
      button.main__OrderButtonRight-n31oe-10 {
        border-radius: 0 8px 8px 0 !important;
      }
      
      /* Both split buttons hover */
      .main__OrderButtonLeft-n31oe-9:hover,
      button.main__OrderButtonLeft-n31oe-9:hover,
      .main__OrderButtonRight-n31oe-10:hover,
      button.main__OrderButtonRight-n31oe-10:hover {
        transform: scale(1.05) !important;
        text-transform: {{buttonHoverTextTransform}} !important;
        {{buttonShadowHover}}
      }
      
      /* Prevent wrapper div from getting styles */
      div.main__OrderButton-n31oe-8 {
        background: transparent !important;
        border: none !important;
      }
      
      div.main__OrderButton-n31oe-8:hover {
        background: transparent !important;
        border: none !important;
        transform: none !important;
        box-shadow: none !important;
      }
    `,
    variables: {
      buttonRestTextTransform: '{{buttonRestTextTransform}}',
      buttonHoverTextTransform: '{{buttonHoverTextTransform}}',
      buttonShadowRest: '{{buttonShadowRest}}',
      buttonShadowHover: '{{buttonShadowHover}}'
    }
  },
  {
    id: 'button-hover-lift',
    category: 'buttonHoverEffect',
    name: 'Lift on Hover',
    description: 'Gentle upward movement on button hover',
    cssTemplate: `
      /* Single button mode (reservations disabled) */
      .sc-bdVaJa, 
      button[color="primary"],
      button.main__OrderButton-n31oe-8 {
        transition: all 0.3s ease;
        position: relative;
        text-transform: {{buttonRestTextTransform}} !important;
        {{buttonShadowRest}}
      }
      
      .sc-bdVaJa:hover, 
      button[color="primary"]:hover,
      button.main__OrderButton-n31oe-8:hover {
        transform: translateY(-2px);
        text-transform: {{buttonHoverTextTransform}} !important;
        {{buttonShadowHover}}
      }
      
      /* Split button mode (reservations enabled) */
      .main__OrderButtonLeft-n31oe-9,
      .main__OrderButtonRight-n31oe-10,
      button.main__OrderButtonLeft-n31oe-9,
      button.main__OrderButtonRight-n31oe-10 {
        transition: all 0.3s ease !important;
        position: relative !important;
        text-transform: {{buttonRestTextTransform}} !important;
        margin: 0 !important;
      }
      
      /* Book button (left) - rounded left corners */
      .main__OrderButtonLeft-n31oe-9,
      button.main__OrderButtonLeft-n31oe-9 {
        border-radius: 8px 0 0 8px !important;
      }
      
      /* Order button (right) - rounded right corners */
      .main__OrderButtonRight-n31oe-10,
      button.main__OrderButtonRight-n31oe-10 {
        border-radius: 0 8px 8px 0 !important;
      }
      
      /* Both split buttons hover */
      .main__OrderButtonLeft-n31oe-9:hover,
      button.main__OrderButtonLeft-n31oe-9:hover,
      .main__OrderButtonRight-n31oe-10:hover,
      button.main__OrderButtonRight-n31oe-10:hover {
        transform: translateY(-2px) !important;
        text-transform: {{buttonHoverTextTransform}} !important;
        {{buttonShadowHover}}
      }
      
      /* Prevent wrapper div from getting styles */
      div.main__OrderButton-n31oe-8 {
        background: transparent !important;
        border: none !important;
      }
      
      div.main__OrderButton-n31oe-8:hover {
        background: transparent !important;
        border: none !important;
        transform: none !important;
        box-shadow: none !important;
      }
    `,
    variables: {
      buttonRestTextTransform: '{{buttonRestTextTransform}}',
      buttonHoverTextTransform: '{{buttonHoverTextTransform}}',
      buttonShadowRest: '{{buttonShadowRest}}',
      buttonShadowHover: '{{buttonShadowHover}}'
    }
  },
  {
    id: 'button-hover-lift-and-color',
    category: 'buttonHoverEffect',
    name: 'Lift and Change Colour',
    description: 'Lift effect with customisable colour changes',
    cssTemplate: `
      /* Single button mode (reservations disabled) */
      .sc-bdVaJa, 
      button[color="primary"],
      button.main__OrderButton-n31oe-8 {
        transition: all 0.3s ease;
        position: relative;
        background: {{buttonRestBg}} !important;
        color: {{buttonRestText}} !important;
        border: 2px solid {{buttonRestBorder}} !important;
        text-transform: {{buttonRestTextTransform}} !important;
        {{buttonShadowRest}}
      }
      
      .sc-bdVaJa:hover, 
      button[color="primary"]:hover,
      button.main__OrderButton-n31oe-8:hover {
        transform: translateY(-2px);
        background: {{buttonHoverBg}} !important;
        color: {{buttonHoverText}} !important;
        border-color: {{buttonHoverBorder}} !important;
        text-transform: {{buttonHoverTextTransform}} !important;
        {{buttonShadowHover}}
      }
      
      /* Split button mode (reservations enabled) */
      .main__OrderButtonLeft-n31oe-9,
      .main__OrderButtonRight-n31oe-10,
      button.main__OrderButtonLeft-n31oe-9,
      button.main__OrderButtonRight-n31oe-10 {
        transition: all 0.3s ease !important;
        position: relative !important;
        background: {{buttonRestBg}} !important;
        color: {{buttonRestText}} !important;
        border: none !important;
        outline: none !important;
        text-transform: {{buttonRestTextTransform}} !important;
        margin: 0 !important;
      }
      
      /* Book button (left) - rounded left corners */
      .main__OrderButtonLeft-n31oe-9,
      button.main__OrderButtonLeft-n31oe-9 {
        border-radius: 8px 0 0 8px !important;
      }
      
      /* Order button (right) - rounded right corners */
      .main__OrderButtonRight-n31oe-10,
      button.main__OrderButtonRight-n31oe-10 {
        border-radius: 0 8px 8px 0 !important;
      }
      
      /* Both split buttons hover */
      .main__OrderButtonLeft-n31oe-9:hover,
      button.main__OrderButtonLeft-n31oe-9:hover,
      .main__OrderButtonRight-n31oe-10:hover,
      button.main__OrderButtonRight-n31oe-10:hover {
        transform: translateY(-2px) !important;
        background: {{buttonHoverBg}} !important;
        color: {{buttonHoverText}} !important;
        text-transform: {{buttonHoverTextTransform}} !important;
        {{buttonShadowHover}}
      }
      
      /* Prevent wrapper div from getting styles */
      div.main__OrderButton-n31oe-8 {
        background: transparent !important;
        border: none !important;
      }
      
      div.main__OrderButton-n31oe-8:hover {
        background: transparent !important;
        border: none !important;
        transform: none !important;
        box-shadow: none !important;
      }
      
      /* Remove borders from parent containers */
      .main__Wrapper-n31oe-0 .hrcdc,
      div.hrcdc {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
      }
    `,
    variables: {
      buttonRestBg: '{{buttonRestBg}}',
      buttonRestText: '{{buttonRestText}}',
      buttonRestBorder: '{{buttonRestBorder}}',
      buttonRestTextTransform: '{{buttonRestTextTransform}}',
      buttonHoverBg: '{{buttonHoverBg}}',
      buttonHoverText: '{{buttonHoverText}}',
      buttonHoverBorder: '{{buttonHoverBorder}}',
      buttonHoverTextTransform: '{{buttonHoverTextTransform}}',
      buttonShadowRest: '{{buttonShadowRest}}',
      buttonShadowHover: '{{buttonShadowHover}}'
    }
  },
  {
    id: 'button-hover-scale-and-color',
    category: 'buttonHoverEffect',
    name: 'Scale and Change Colour',
    description: 'Scale effect with customisable colour changes',
    cssTemplate: `
      /* Single button mode (reservations disabled) */
      .sc-bdVaJa, 
      button[color="primary"],
      button.main__OrderButton-n31oe-8 {
        transition: all 0.3s ease;
        position: relative;
        background: {{buttonRestBg}} !important;
        color: {{buttonRestText}} !important;
        border: 2px solid {{buttonRestBorder}} !important;
        text-transform: {{buttonRestTextTransform}} !important;
        {{buttonShadowRest}}
      }
      
      .sc-bdVaJa:hover, 
      button[color="primary"]:hover,
      button.main__OrderButton-n31oe-8:hover {
        transform: scale(1.05);
        background: {{buttonHoverBg}} !important;
        color: {{buttonHoverText}} !important;
        border-color: {{buttonHoverBorder}} !important;
        text-transform: {{buttonHoverTextTransform}} !important;
        {{buttonShadowHover}}
      }
      
      /* Split button mode (reservations enabled) */
      .main__OrderButtonLeft-n31oe-9,
      .main__OrderButtonRight-n31oe-10,
      button.main__OrderButtonLeft-n31oe-9,
      button.main__OrderButtonRight-n31oe-10 {
        transition: all 0.3s ease !important;
        position: relative !important;
        background: {{buttonRestBg}} !important;
        color: {{buttonRestText}} !important;
        border: none !important;
        outline: none !important;
        text-transform: {{buttonRestTextTransform}} !important;
        margin: 0 !important;
      }
      
      /* Book button (left) - rounded left corners */
      .main__OrderButtonLeft-n31oe-9,
      button.main__OrderButtonLeft-n31oe-9 {
        border-radius: 8px 0 0 8px !important;
      }
      
      /* Order button (right) - rounded right corners */
      .main__OrderButtonRight-n31oe-10,
      button.main__OrderButtonRight-n31oe-10 {
        border-radius: 0 8px 8px 0 !important;
      }
      
      /* Both split buttons hover */
      .main__OrderButtonLeft-n31oe-9:hover,
      button.main__OrderButtonLeft-n31oe-9:hover,
      .main__OrderButtonRight-n31oe-10:hover,
      button.main__OrderButtonRight-n31oe-10:hover {
        transform: scale(1.05) !important;
        background: {{buttonHoverBg}} !important;
        color: {{buttonHoverText}} !important;
        text-transform: {{buttonHoverTextTransform}} !important;
        {{buttonShadowHover}}
      }
      
      /* Prevent wrapper div from getting styles */
      div.main__OrderButton-n31oe-8 {
        background: transparent !important;
        border: none !important;
      }
      
      div.main__OrderButton-n31oe-8:hover {
        background: transparent !important;
        border: none !important;
        transform: none !important;
        box-shadow: none !important;
      }
      
      /* Remove borders from parent containers */
      .main__Wrapper-n31oe-0 .hrcdc,
      div.hrcdc {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
      }
    `,
    variables: {
      buttonRestBg: '{{buttonRestBg}}',
      buttonRestText: '{{buttonRestText}}',
      buttonRestBorder: '{{buttonRestBorder}}',
      buttonRestTextTransform: '{{buttonRestTextTransform}}',
      buttonHoverBg: '{{buttonHoverBg}}',
      buttonHoverText: '{{buttonHoverText}}',
      buttonHoverBorder: '{{buttonHoverBorder}}',
      buttonHoverTextTransform: '{{buttonHoverTextTransform}}',
      buttonShadowRest: '{{buttonShadowRest}}',
      buttonShadowHover: '{{buttonShadowHover}}'
    }
  }
];

// Button Click Effect Templates (Multi-Selection - Button click animations)
export const buttonClickEffect: ComponentTemplate[] = [
  {
    id: 'button-click-ripple',
    category: 'buttonClickEffect',
    name: 'Ripple Effect',
    description: 'Ripple animation on button click',
    jsTemplate: `
      // Add ripple effect to buttons
      function createRipple(event) {
        const button = event.currentTarget;
        
        const circle = document.createElement("span");
        const diameter = Math.max(button.clientWidth, button.clientHeight);
        const radius = diameter / 2;
        
        circle.style.width = circle.style.height = \`\${diameter}px\`;
        circle.style.left = \`\${event.clientX - button.getBoundingClientRect().left - radius}px\`;
        circle.style.top = \`\${event.clientY - button.getBoundingClientRect().top - radius}px\`;
        circle.classList.add("ripple");
        
        const ripple = button.getElementsByClassName("ripple")[0];
        
        if (ripple) {
          ripple.remove();
        }
        
        button.appendChild(circle);
      }
      
      // Add ripple style
      const style = document.createElement('style');
      style.textContent = \`
        .ripple {
          position: absolute;
          background-color: rgba(255, 255, 255, 0.4);
          border-radius: 50%;
          transform: scale(0);
          animation: ripple {{animationDuration}}s linear;
          pointer-events: none;
        }
        
        @keyframes ripple {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
        
        .sc-bdVaJa, button[color="primary"], button {
          position: relative;
          overflow: hidden;
        }
      \`;
      document.head.appendChild(style);
      
      // Apply ripple effect to primary buttons
      const buttons = document.querySelectorAll(".sc-bdVaJa, button[color='primary'], button");
      buttons.forEach(button => {
        button.addEventListener("click", createRipple);
      });
    `,
    variables: {
      animationDuration: '{{animationDuration}}'
    },
    stackable: true
  }
];

// Button Pulse Effect Templates (Multi-Selection - Continuous button animations)
export const buttonPulseEffect: ComponentTemplate[] = [
  {
    id: 'button-pulse-glow',
    category: 'buttonPulseEffect',
    name: 'Pulse Glow',
    description: 'Continuous pulsing glow effect with brand colours',
    cssTemplate: `
      #order-button, .order-now-button, .cta-button {
        animation: brandPulseGlow 2s ease-in-out infinite;
      }
      
      @keyframes brandPulseGlow {
        0%, 100% { 
          box-shadow: 0 0 0 0 rgba({{primaryColorRgb}}, 0.4), 0 4px 10px rgba({{primaryColorRgb}}, 0.2); 
        }
        50% { 
          box-shadow: 0 0 0 8px rgba({{primaryColorRgb}}, 0), 0 6px 15px rgba({{primaryColorRgb}}, 0.4); 
        }
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColor: '{{primaryColor}}',
      primaryColorRgb: '{{primaryColorRgb}}'
    },
    stackable: true
  },
  {
    id: 'button-pulse-dual-brand',
    category: 'buttonPulseEffect',
    name: 'Dual Brand Pulse',
    description: 'Pulsing effect using both brand colours',
    cssTemplate: `
      #order-button, .order-now-button, .cta-button {
        animation: dualBrandPulse 3s ease-in-out infinite;
      }
      
      @keyframes dualBrandPulse {
        0%, 100% { 
          box-shadow: 0 0 0 0 rgba({{primaryColorRgb}}, 0.4), 0 4px 10px rgba({{primaryColorRgb}}, 0.2); 
        }
        25% { 
          box-shadow: 0 0 0 6px rgba({{primaryColorRgb}}, 0.1), 0 6px 15px rgba({{primaryColorRgb}}, 0.4); 
        }
        50% { 
          box-shadow: 0 0 0 8px rgba({{secondaryColorRgb}}, 0), 0 6px 15px rgba({{secondaryColorRgb}}, 0.4); 
        }
        75% { 
          box-shadow: 0 0 0 6px rgba({{secondaryColorRgb}}, 0.1), 0 6px 15px rgba({{secondaryColorRgb}}, 0.4); 
        }
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColor: '{{primaryColor}}',
      secondaryColor: '{{secondaryColor}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      secondaryColorRgb: '{{secondaryColorRgb}}'
    },
    stackable: true
  },
  {
    id: 'button-subtle-pulse',
    category: 'buttonPulseEffect',
    name: 'Subtle Pulse',
    description: 'Gentle pulsing effect for all buttons',
    cssTemplate: `
      button, .button, [role="button"], .sc-bdVaJa {
        animation: subtlePulse 4s ease-in-out infinite;
      }
      
      @keyframes subtlePulse {
        0%, 100% { 
          transform: scale(1);
          box-shadow: 0 4px 10px rgba({{primaryColorRgb}}, 0.2); 
        }
        50% { 
          transform: scale(1.02);
          box-shadow: 0 6px 15px rgba({{primaryColorRgb}}, 0.3); 
        }
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      movementStrength: '{{movementStrength}}'
    },
    stackable: true
  }
];

// Initial Fade Style Templates (Multi-Selection - Page load fade animations)
export const initialFadeStyle: ComponentTemplate[] = [
  {
    id: 'page-fade-in',
    category: 'initialFadeStyle',
    name: 'Page Fade In',
    description: 'Smooth fade-in effect for entire page',
    cssTemplate: `
      body {
        opacity: 0;
        animation: pageFadeIn {{animationDuration}}s ease forwards;
      }
      
      @keyframes pageFadeIn {
        to { opacity: 1; }
      }
    `,
    variables: {
      animationDuration: '{{animationDuration}}'
    },
    stackable: true
  }
];

// Stagger Timing Templates (Multi-Selection - Staggered animation timing)
export const staggerTiming: ComponentTemplate[] = [
  {
    id: 'stagger-menu-items',
    category: 'staggerTiming',
    name: 'Staggered Menu Items',
    description: 'Staggered animation timing for menu items',
    cssTemplate: `
      .item__DishComponent-wkeq8p-0:nth-child(1) { animation-delay: 0s; }
      .item__DishComponent-wkeq8p-0:nth-child(2) { animation-delay: 0.1s; }
      .item__DishComponent-wkeq8p-0:nth-child(3) { animation-delay: 0.2s; }
      .item__DishComponent-wkeq8p-0:nth-child(4) { animation-delay: 0.3s; }
      .item__DishComponent-wkeq8p-0:nth-child(5) { animation-delay: 0.4s; }
      .item__DishComponent-wkeq8p-0:nth-child(n+6) { animation-delay: 0.5s; }
    `,
    variables: {},
    stackable: true
  }
];

// =============================================================================
// PHASE 2 ENHANCEMENTS
// =============================================================================

// Price Tag Enhancement Templates (Multi-Selection - Price tag styling with gradients and animations)
export const priceTagEnhancements: ComponentTemplate[] = [
  {
    id: 'price-tag-gradient',
    category: 'priceTagEnhancements',
    name: 'Gradient Background',
    description: 'Price tags with brand colour gradients',
    cssTemplate: `
      .item__Price-wkeq8p-6 {
        font-weight: bold;
        background: linear-gradient({{priceTagGradientDirection}}, {{priceTagGradientStartColor}} 0%, {{priceTagGradientEndColor}} 100%);
        box-shadow: 0 4px 10px rgba({{primaryColorRgb}}, 0.3);
        padding: 4px 8px !important;
      }
      
      .item__Price-wkeq8p-6 p {
        color: {{priceTagGradientTextColor}} !important;
        font-weight: 700 !important;
        position: relative;
        z-index: 1;
      }
    `,
    variables: {
      primaryColor: '{{primaryColor}}',
      secondaryColor: '{{secondaryColor}}',
      primaryColorRgb: '{{primaryColorRgb}}',
      priceTagGradientDirection: '{{priceTagGradientDirection}}',
      priceTagGradientStartColor: '{{priceTagGradientStartColor}}',
      priceTagGradientEndColor: '{{priceTagGradientEndColor}}',
      priceTagGradientTextColor: '{{priceTagGradientTextColor}}'
    },
    stackable: true
  },
  {
    id: 'price-tag-single-color',
    category: 'priceTagEnhancements',
    name: 'Single Colour Background',
    description: 'Simple solid colour price tags with customisable background and text colours',
    cssTemplate: `
      .item__Price-wkeq8p-6 {
        font-weight: bold;
        background: {{priceTagBackgroundColor}} !important;
        padding: 4px 8px !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
      
      .item__Price-wkeq8p-6 p {
        color: {{priceTagTextColor}} !important;
        font-weight: 700 !important;
      }
    `,
    variables: {
      priceTagBackgroundColor: '{{priceTagBackgroundColor}}',
      priceTagTextColor: '{{priceTagTextColor}}'
    },
    stackable: true
  }
];

// Nav Bar Text Effects Templates (Multi-Selection - Logo text gradients and effects)
export const navBarTextEffects: ComponentTemplate[] = [
  {
    id: 'nav-logo-gradient',
    category: 'navBarTextEffects',
    name: 'Logo Text Gradient',
    description: 'Gradient text effect on navigation logo',
    cssTemplate: `
      .main__Logo-n31oe-2 span {
        font-weight: 800 !important;
        letter-spacing: -0.5px;
        text-transform: uppercase;
        font-size: 20px !important;
        background: linear-gradient(45deg, {{navLogoGradientPrimary}}, {{navLogoGradientSecondary}});
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        transition: all 0.3s ease;
      }
      
      .main__Logo-n31oe-2:hover span {
        background: linear-gradient(45deg, {{navLogoGradientPrimary}}, {{navLogoGradientSecondary}});
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        transform: scale(1.05);
        filter: brightness(1.2);
      }
    `,
    variables: {
      navLogoGradientPrimary: '{{navLogoGradientPrimary}}',
      navLogoGradientSecondary: '{{navLogoGradientSecondary}}'
    },
    stackable: true
  },
  {
    id: 'nav-logo-text-glow',
    category: 'navBarTextEffects',
    name: 'Logo Text Glow Effect',
    description: 'Glowing text shadow on logo text',
    cssTemplate: `
      .main__Logo-n31oe-2 span {
        text-shadow: 0 0 10px rgba({{navLogoGlowColorRgb}}, 0.5);
        transition: all 0.3s ease;
      }
      
      .main__Logo-n31oe-2:hover span {
        text-shadow: 0 0 20px rgba({{navLogoGlowColorRgb}}, 0.8);
      }
    `,
    variables: {
      navLogoGlowColorRgb: '{{navLogoGlowColorRgb}}'
    },
    stackable: true
  },
  {
    id: 'nav-logo-glow',
    category: 'navBarTextEffects',
    name: 'Logo Glow Effect',
    description: 'Glowing shadow effect on logo image for dark background visibility',
    cssTemplate: `
      .main__Logo-n31oe-2 img {
        filter: drop-shadow(0 0 10px rgba({{primaryColorRgb}}, 1));
        transition: all 0.3s ease;
      }
      
      .main__Logo-n31oe-2:hover img {
        filter: drop-shadow(0 0 10px rgba(255, 255, 255, 1));
      }
    `,
    variables: {
      primaryColorRgb: '{{primaryColorRgb}}'
    },
    stackable: true
  },
  {
    id: 'nav-logo-scale',
    category: 'navBarTextEffects',
    name: 'Logo Scale Animation',
    description: 'Subtle scaling animation on logo hover',
    cssTemplate: `
      .main__Logo-n31oe-2 {
        transition: transform 0.3s ease;
      }

      .main__Logo-n31oe-2:hover {
        transform: scale(1.05);
      }
    `,
    variables: {},
    stackable: true
  },
  {
    id: 'nav-logo-image-hover-scale',
    category: 'navBarTextEffects',
    name: 'Logo Image Hover Scale',
    description: 'Subtle scaling animation on logo image hover',
    jsTemplate: `
      // Add subtle logo animation
      const logo = document.querySelector('#header-logo-image');
      if (logo) {
        logo.style.transition = 'all 0.5s ease';
        logo.addEventListener('mouseover', function() {
         this.style.transform = 'scale(1.05)';
        });
        logo.addEventListener('mouseout', function() {
          this.style.transform = 'scale(1)';
        });
      }
    `,
    variables: {},
    stackable: true
  }
];

// Welcome Messages Templates (Multi-Selection - Time-based greeting notifications)
export const welcomeMessages: ComponentTemplate[] = [
  {
    id: 'welcome-greeting-basic',
    category: 'welcomeMessages',
    name: 'Time-Based Greeting',
    description: 'Time-based greeting with cultural variations',
    jsTemplate: `
      // Customisable time-based greeting message
      setTimeout(() => {
        const timeOfDay = new Date().getHours();
        let greeting = '';
        
        // Use customisable time thresholds and messages
        if (timeOfDay < {{welcomeMessageMorningHour}}) {
          greeting = '{{welcomeMessageMorningText}}';
        } else if (timeOfDay < {{welcomeMessageAfternoonHour}}) {
          greeting = '{{welcomeMessageAfternoonText}}';
        } else {
          greeting = '{{welcomeMessageEveningText}}';
        }
        
        const greetingDiv = document.createElement('div');
        greetingDiv.style.position = 'fixed';
        greetingDiv.style.top = '160px';
        greetingDiv.style.right = '20px';
        greetingDiv.style.padding = '15px 25px';
        greetingDiv.style.background = 'linear-gradient(135deg, {{welcomeMessageGradientStartColor}}, {{welcomeMessageGradientEndColor}})';
        greetingDiv.style.borderRadius = '12px';
        greetingDiv.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
        greetingDiv.style.zIndex = '1000';
        greetingDiv.style.opacity = '0';
        greetingDiv.style.transform = 'translateX(100%)';
        greetingDiv.style.transition = 'all 0.5s ease';
        greetingDiv.style.fontFamily = 'inherit';
        greetingDiv.style.fontSize = '16px';
        {{welcomeMessageBorderStyle}}
        greetingDiv.innerHTML = \`<strong style="color: {{welcomeMessageTextColor}}; font-size: 16px;">\${greeting}</strong><br><small style="color: {{welcomeMessageTextColor}}; font-size: 14px;">{{welcomeMessageSecondaryText}}</small>\`;
        
        document.body.appendChild(greetingDiv);
        
        setTimeout(() => {
          greetingDiv.style.opacity = '1';
          greetingDiv.style.transform = 'translateX(0)';
        }, 100);
        
        setTimeout(() => {
          greetingDiv.style.opacity = '0';
          greetingDiv.style.transform = 'translateX(100%)';
          setTimeout(() => greetingDiv.remove(), 500);
        }, 4000);
      }, 1500);
    `,
    variables: {
      welcomeMessageMorningText: '{{welcomeMessageMorningText}}',
      welcomeMessageAfternoonText: '{{welcomeMessageAfternoonText}}',
      welcomeMessageEveningText: '{{welcomeMessageEveningText}}',
      welcomeMessageSecondaryText: '{{welcomeMessageSecondaryText}}',
      welcomeMessageMorningHour: '{{welcomeMessageMorningHour}}',
      welcomeMessageAfternoonHour: '{{welcomeMessageAfternoonHour}}',
      welcomeMessageEveningHour: '{{welcomeMessageEveningHour}}',
      welcomeMessageTextColor: '{{welcomeMessageTextColor}}',
      welcomeMessageGradientStartColor: '{{welcomeMessageGradientStartColor}}',
      welcomeMessageGradientEndColor: '{{welcomeMessageGradientEndColor}}',
      welcomeMessageBorderColor: '{{welcomeMessageBorderColor}}',
      welcomeMessageBorderStyle: '{{welcomeMessageBorderStyle}}'
    },
    stackable: true
  }
];

// Tag Badge Styles Templates (Multi-Selection - Enhanced food tag styling)
export const tagBadgeStyles: ComponentTemplate[] = [
  {
    id: 'tag-badge-enhanced',
    category: 'tagBadgeStyles',
    name: 'Enhanced Badge Styling',
    description: 'Improved styling for food tags with gradients',
    cssTemplate: `
      /* Support both Top Edge (.item__Tags-wkeq8p-2) and Inner Bottom (.item__InnerTags-wkeq8p-3) positions */

      /* Reset wrapper elements to be transparent so only the text element shows styling */
      /* Also handle hover states to prevent background color from appearing */
      .item__Tags-wkeq8p-2 .dishtag__Wrapper-bxVoZd,
      .item__InnerTags-wkeq8p-3 .dishtag__Wrapper-bxVoZd,
      .item__Tags-wkeq8p-2 .dishtag__Content-impgUC,
      .item__InnerTags-wkeq8p-3 .dishtag__Content-impgUC,
      .item__Tags-wkeq8p-2 .dishtag__Wrapper-bxVoZd:hover,
      .item__InnerTags-wkeq8p-3 .dishtag__Wrapper-bxVoZd:hover,
      .item__Tags-wkeq8p-2 .dishtag__Content-impgUC:hover,
      .item__InnerTags-wkeq8p-3 .dishtag__Content-impgUC:hover {
        background: transparent !important;
        background-color: transparent !important;
        border: none !important;
        padding: 0 !important;
        margin: 0 !important;
        box-shadow: none !important;
      }

      /* Style the actual tag text elements (.dishtag__Text-htARsz) */
      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz,
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz {
        /* Using component-specific colors for enhanced tag badges */
        background: linear-gradient(135deg, rgba({{tagBadgeEnhancedPrimaryRgb}}, 0.9), rgba({{tagBadgeEnhancedSecondaryRgb}}, 0.9));
        border: 1px solid rgba({{tagBadgeEnhancedPrimaryRgb}}, 0.4);
        padding: 4px 10px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        transition: all 0.3s ease;
        display: inline-block;
        margin: 2px;
      }

      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz:hover,
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz:hover {
        /* Hover state remains fully opaque */
        background: linear-gradient(135deg, rgba({{tagBadgeEnhancedPrimaryRgb}}, 1), rgba({{tagBadgeEnhancedSecondaryRgb}}, 1));
        transform: translateY(-2px) scale(1.05);
        box-shadow: 0 4px 8px rgba({{tagBadgeEnhancedPrimaryRgb}}, 0.3);
      }
    `,
    variables: {
      tagBadgeEnhancedPrimaryColor: '{{tagBadgeEnhancedPrimaryColor}}',
      tagBadgeEnhancedSecondaryColor: '{{tagBadgeEnhancedSecondaryColor}}',
      tagBadgeEnhancedPrimaryRgb: '{{tagBadgeEnhancedPrimaryRgb}}',
      tagBadgeEnhancedSecondaryRgb: '{{tagBadgeEnhancedSecondaryRgb}}'
    },
    stackable: true
  },
  {
    id: 'tag-badge-special',
    category: 'tagBadgeStyles',
    name: 'Special Badge Styling',
    description: 'Special styling for vegan and dietary tags',
    cssTemplate: `
      /* Special dietary tag styling using data attributes set by JavaScript */
      /* Support both Top Edge and Inner Bottom positions */

      /* Reset wrapper elements to be transparent so only the text element shows styling */
      /* Also handle hover states to prevent background color from appearing */
      .item__Tags-wkeq8p-2 .dishtag__Wrapper-bxVoZd,
      .item__InnerTags-wkeq8p-3 .dishtag__Wrapper-bxVoZd,
      .item__Tags-wkeq8p-2 .dishtag__Content-impgUC,
      .item__InnerTags-wkeq8p-3 .dishtag__Content-impgUC,
      .item__Tags-wkeq8p-2 .dishtag__Wrapper-bxVoZd:hover,
      .item__InnerTags-wkeq8p-3 .dishtag__Wrapper-bxVoZd:hover,
      .item__Tags-wkeq8p-2 .dishtag__Content-impgUC:hover,
      .item__InnerTags-wkeq8p-3 .dishtag__Content-impgUC:hover {
        background: transparent !important;
        background-color: transparent !important;
        border: none !important;
        padding: 0 !important;
        margin: 0 !important;
        box-shadow: none !important;
      }

      /* Vegan tags - Green gradient */
      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz[data-dietary-tag="vegan"],
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz[data-dietary-tag="vegan"] {
        background: linear-gradient(135deg, {{tagBadgeVeganPrimaryColor}}, {{tagBadgeVeganSecondaryColor}}) !important;
        color: white !important;
        border-color: {{tagBadgeVeganSecondaryColor}} !important;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba({{tagBadgeVeganPrimaryRgb}}, 0.3);
      }

      /* Vegetarian tags - Green gradient */
      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz[data-dietary-tag="vegetarian"],
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz[data-dietary-tag="vegetarian"] {
        background: linear-gradient(135deg, {{tagBadgeVegetarianPrimaryColor}}, {{tagBadgeVegetarianSecondaryColor}}) !important;
        color: white !important;
        border-color: {{tagBadgeVegetarianSecondaryColor}} !important;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba({{tagBadgeVegetarianPrimaryRgb}}, 0.3);
      }

      /* Gluten-free tags - Orange gradient */
      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz[data-dietary-tag="gluten-free"],
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz[data-dietary-tag="gluten-free"] {
        background: linear-gradient(135deg, {{tagBadgeGlutenFreePrimaryColor}}, {{tagBadgeGlutenFreeSecondaryColor}}) !important;
        color: white !important;
        border-color: {{tagBadgeGlutenFreeSecondaryColor}} !important;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba({{tagBadgeGlutenFreeSecondaryRgb}}, 0.3);
      }

      /* Spicy/Hot tags - Red gradient */
      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz[data-dietary-tag="spicy"],
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz[data-dietary-tag="spicy"],
      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz[data-dietary-tag="hot"],
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz[data-dietary-tag="hot"] {
        background: linear-gradient(135deg, {{tagBadgeSpicyPrimaryColor}}, {{tagBadgeSpicySecondaryColor}}) !important;
        color: white !important;
        border-color: {{tagBadgeSpicySecondaryColor}} !important;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba({{tagBadgeSpicySecondaryRgb}}, 0.3);
      }

      /* Dairy-free tags - Blue gradient */
      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz[data-dietary-tag="dairy-free"],
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz[data-dietary-tag="dairy-free"] {
        background: linear-gradient(135deg, {{tagBadgeDairyFreePrimaryColor}}, {{tagBadgeDairyFreeSecondaryColor}}) !important;
        color: white !important;
        border-color: {{tagBadgeDairyFreeSecondaryColor}} !important;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba({{tagBadgeDairyFreeSecondaryRgb}}, 0.3);
      }

      /* Nut-free tags - Brown gradient */
      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz[data-dietary-tag="nut-free"],
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz[data-dietary-tag="nut-free"] {
        background: linear-gradient(135deg, {{tagBadgeNutFreePrimaryColor}}, {{tagBadgeNutFreeSecondaryColor}}) !important;
        color: white !important;
        border-color: {{tagBadgeNutFreeSecondaryColor}} !important;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba({{tagBadgeNutFreeSecondaryRgb}}, 0.3);
      }

      /* Halal tags - Green gradient */
      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz[data-dietary-tag="halal"],
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz[data-dietary-tag="halal"] {
        background: linear-gradient(135deg, {{tagBadgeHalalPrimaryColor}}, {{tagBadgeHalalSecondaryColor}}) !important;
        color: white !important;
        border-color: {{tagBadgeHalalSecondaryColor}} !important;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba({{tagBadgeHalalSecondaryRgb}}, 0.3);
      }

      /* Popular tags - Purple to Red gradient */
      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz[data-dietary-tag="popular"],
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz[data-dietary-tag="popular"] {
        background: linear-gradient(135deg, {{tagBadgePopularPrimaryColor}}, {{tagBadgePopularSecondaryColor}}) !important;
        color: white !important;
        border-color: {{tagBadgePopularSecondaryColor}} !important;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba({{tagBadgePopularSecondaryRgb}}, 0.3);
      }

      /* New tags - Red to Blue gradient */
      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz[data-dietary-tag="new"],
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz[data-dietary-tag="new"] {
        background: linear-gradient(135deg, {{tagBadgeNewPrimaryColor}}, {{tagBadgeNewSecondaryColor}}) !important;
        color: white !important;
        border-color: {{tagBadgeNewSecondaryColor}} !important;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba({{tagBadgeNewSecondaryRgb}}, 0.3);
      }

      /* Deal tags - Green to Red gradient */
      .item__Tags-wkeq8p-2 .dishtag__Text-htARsz[data-dietary-tag="deal"],
      .item__InnerTags-wkeq8p-3 .dishtag__Text-htARsz[data-dietary-tag="deal"] {
        background: linear-gradient(135deg, {{tagBadgeDealPrimaryColor}}, {{tagBadgeDealSecondaryColor}}) !important;
        color: white !important;
        border-color: {{tagBadgeDealSecondaryColor}} !important;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba({{tagBadgeDealSecondaryRgb}}, 0.3);
      }
    `,
    jsTemplate: `
      // Tag detection and data attribute assignment for special dietary tags
      (function() {
        // Function to process tags and add data attributes based on text content
        function processTagElements() {
          // Select all tag text elements in both Top Edge and Inner Bottom positions
          const tagElements = document.querySelectorAll('.dishtag__Text-htARsz');

          tagElements.forEach(tag => {
            // Skip if already processed
            if (tag.hasAttribute('data-dietary-tag')) {
              return;
            }

            // Get tag text and convert to lowercase for matching
            const tagText = tag.textContent.toLowerCase().trim();

            // Match dietary tags and assign appropriate data attributes
            // TODO: In Phase 2, make these mappings configurable through the UI
            if (tagText.includes('vegan')) {
              tag.setAttribute('data-dietary-tag', 'vegan');
            } else if (tagText.includes('vegetarian')) {
              tag.setAttribute('data-dietary-tag', 'vegetarian');
            } else if (tagText.includes('gluten') && tagText.includes('free')) {
              tag.setAttribute('data-dietary-tag', 'gluten-free');
            } else if (tagText.includes('gluten') && tagText.includes('no')) {
              tag.setAttribute('data-dietary-tag', 'gluten-free');  
            } else if (tagText === 'gf' || tagText === 'gluten free') {
              tag.setAttribute('data-dietary-tag', 'gluten-free');
            } else if (tagText.includes('spicy') || tagText.includes('hot') || tagText === '🌶️' || tagText === '🔥') {
              tag.setAttribute('data-dietary-tag', 'spicy');
            } else if (tagText.includes('dairy') && tagText.includes('free')) {
              tag.setAttribute('data-dietary-tag', 'dairy-free');
            } else if (tagText === 'df' || tagText === 'dairy free') {
              tag.setAttribute('data-dietary-tag', 'dairy-free');
            } else if (tagText.includes('nut') && tagText.includes('free')) {
              tag.setAttribute('data-dietary-tag', 'nut-free');
            } else if (tagText === 'nf' || tagText === 'nut free') {
              tag.setAttribute('data-dietary-tag', 'nut-free');
            } else if (tagText.includes('halal')) {
              tag.setAttribute('data-dietary-tag', 'halal');
            }
            // Popular tags: popular, most liked, favourite, recommended, trending, highly rated, specialty
            else if (tagText.includes('popular') || tagText.includes('most liked') ||
                     tagText.includes('favourite') || tagText.includes('must try') ||
                     tagText.includes('recommended') || tagText.includes('trending') ||
                     tagText.includes('highly rated') || tagText.includes('specialty') ||
                     tagText === '⭐' || tagText === '👍' || tagText === '❤️') {
              tag.setAttribute('data-dietary-tag', 'popular');
            }
            // New tags: new, limited time, limited time only, seasonal, while stock lasts, today only
            else if (tagText === 'new' || tagText.includes('limited time') ||
                     tagText.includes('seasonal') || tagText.includes('while stock lasts') ||
                     tagText.includes('today only') || tagText === '🆕' || tagText === '⚡') {
              tag.setAttribute('data-dietary-tag', 'new');
            }
            // Deal tags: deal, promo, promotion, special, buy one get one, buy 1 get 1, 2 for 1, two for one, combo, free item, free gift
            else if (tagText.includes('deal') || tagText.includes('promo') ||
                     tagText.includes('promotion') || tagText.includes('special') ||
                     tagText.includes('buy one get one') || tagText.includes('buy 1 get 1') ||
                     tagText.includes('2 for 1') || tagText.includes('two for one') ||
                     tagText.includes('combo') || tagText.includes('free item') ||
                     tagText.includes('free gift') || tagText.includes('discount') ||
                     tagText === '💰' || tagText === '🏷️' || tagText === '%') {
              tag.setAttribute('data-dietary-tag', 'deal');
            }
          });
        }

        // Initial processing of existing tags
        processTagElements();

        // Set up MutationObserver to watch for dynamically added tags
        const observer = new MutationObserver((mutations) => {
          // Check if any new tags were added
          const hasNewTags = mutations.some(mutation => {
            return Array.from(mutation.addedNodes).some(node => {
              return node.nodeType === 1 && (
                node.classList?.contains('dishtag__Text-htARsz') ||
                node.querySelector?.('.dishtag__Text-htARsz')
              );
            });
          });

          // Process tags if new ones were added
          if (hasNewTags) {
            processTagElements();
          }
        });

        // Start observing the document body for changes
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
      })();
    `,
    variables: {
      // Tag badge color variables
      tagBadgeVeganPrimaryColor: '{{tagBadgeVeganPrimaryColor}}',
      tagBadgeVeganSecondaryColor: '{{tagBadgeVeganSecondaryColor}}',
      tagBadgeVeganPrimaryRgb: '{{tagBadgeVeganPrimaryRgb}}',
      tagBadgeVegetarianPrimaryColor: '{{tagBadgeVegetarianPrimaryColor}}',
      tagBadgeVegetarianSecondaryColor: '{{tagBadgeVegetarianSecondaryColor}}',
      tagBadgeGlutenFreePrimaryColor: '{{tagBadgeGlutenFreePrimaryColor}}',
      tagBadgeGlutenFreeSecondaryColor: '{{tagBadgeGlutenFreeSecondaryColor}}',
      tagBadgeGlutenFreeSecondaryRgb: '{{tagBadgeGlutenFreeSecondaryRgb}}',
      tagBadgeSpicyPrimaryColor: '{{tagBadgeSpicyPrimaryColor}}',
      tagBadgeSpicySecondaryColor: '{{tagBadgeSpicySecondaryColor}}',
      tagBadgeSpicySecondaryRgb: '{{tagBadgeSpicySecondaryRgb}}',
      tagBadgeDairyFreePrimaryColor: '{{tagBadgeDairyFreePrimaryColor}}',
      tagBadgeDairyFreeSecondaryColor: '{{tagBadgeDairyFreeSecondaryColor}}',
      tagBadgeDairyFreeSecondaryRgb: '{{tagBadgeDairyFreeSecondaryRgb}}',
      tagBadgeNutFreePrimaryColor: '{{tagBadgeNutFreePrimaryColor}}',
      tagBadgeNutFreeSecondaryColor: '{{tagBadgeNutFreeSecondaryColor}}',
      tagBadgeNutFreeSecondaryRgb: '{{tagBadgeNutFreeSecondaryRgb}}',
      tagBadgeHalalPrimaryColor: '{{tagBadgeHalalPrimaryColor}}',
      tagBadgeHalalSecondaryColor: '{{tagBadgeHalalSecondaryColor}}',
      tagBadgeHalalSecondaryRgb: '{{tagBadgeHalalSecondaryRgb}}',
      tagBadgePopularPrimaryColor: '{{tagBadgePopularPrimaryColor}}',
      tagBadgePopularSecondaryColor: '{{tagBadgePopularSecondaryColor}}',
      tagBadgePopularSecondaryRgb: '{{tagBadgePopularSecondaryRgb}}',
      tagBadgeNewPrimaryColor: '{{tagBadgeNewPrimaryColor}}',
      tagBadgeNewSecondaryColor: '{{tagBadgeNewSecondaryColor}}',
      tagBadgeNewSecondaryRgb: '{{tagBadgeNewSecondaryRgb}}',
      tagBadgeDealPrimaryColor: '{{tagBadgeDealPrimaryColor}}',
      tagBadgeDealSecondaryColor: '{{tagBadgeDealSecondaryColor}}',
      tagBadgeDealSecondaryRgb: '{{tagBadgeDealSecondaryRgb}}'
    },
    stackable: true
  }
];

// Error Message Styles Templates (Multi-Selection - Warning/error message enhancements)
export const errorMessageStyles: ComponentTemplate[] = [
  {
    id: 'error-message-enhanced',
    category: 'errorMessageStyles',
    name: 'Enhanced Error Styling',
    description: 'Improved styling for error and warning messages',
    cssTemplate: `
      .error-text,
      .warning-text,
      .alert-danger,
      .alert-warning {
        background: rgba(189, 54, 47, 0.1);
        padding: 2px 4px;
        border-radius: 8px;
        display: inline-block;
        border: 2px solid rgba(189, 54, 47, 0.3);
        font-weight: 600;
        box-shadow: 0 2px 8px rgba(189, 54, 47, 0.2);
        transition: all 0.3s ease;
      }
      
      .error-text:hover,
      .warning-text:hover {
        background: rgba(189, 54, 47, 0.15);
        border-color: rgba(189, 54, 47, 0.5);
        transform: translateY(-1px);
      }
      
      /* Out of stock specific styling */
      .error-text {
        background: linear-gradient(135deg, rgba(189, 54, 47, 0.1), rgba(220, 53, 69, 0.1));
        border: 2px solid rgba(189, 54, 47, 0.4);
        color: #bd362f;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    `,
    variables: {},
    stackable: true
  },
  {
    id: 'error-message-animated',
    category: 'errorMessageStyles',
    name: 'Animated Error Messages',
    description: 'Subtle animations for error messages',
    cssTemplate: `
      .error-text,
      .warning-text {
        animation: errorPulse 2s ease-in-out infinite;
      }
      
      @keyframes errorPulse {
        0%, 100% { 
          opacity: 1;
          transform: scale(1);
        }
        50% { 
          opacity: 0.8;
          transform: scale(1.02);
        }
      }
      
      /* Phone validation error styling */
      .phone-error-message {
        background: linear-gradient(135deg, rgba(30, 167, 46, 0.1), rgba(40, 167, 69, 0.1)) !important;
        border: 2px solid rgba(30, 167, 46, 0.4) !important;
        color: #1ea72e !important;
        animation: validationGlow 1.5s ease-in-out infinite;
      }
      
      @keyframes validationGlow {
        0%, 100% { box-shadow: 0 0 5px rgba(30, 167, 46, 0.3); }
        50% { box-shadow: 0 0 15px rgba(30, 167, 46, 0.5); }
      }
    `,
    variables: {},
    stackable: true
  }
];

// Loading Spinner Styles Templates (Multi-Selection - Custom loading spinner colors)
export const loadingSpinnerStyles: ComponentTemplate[] = [
  {
    id: 'loading-spinner-brand',
    category: 'loadingSpinnerStyles',
    name: 'Brand Colour Spinner',
    description: 'Loading spinners with brand colours',
    cssTemplate: `
      .blQQcr, .jVKOUv,
      .sc-bwzfXH .bounce1,
      .sc-bwzfXH .bounce2,
      .sc-bwzfXH .bounce3 {
        background-color: {{primaryColor}} !important;
        border-color: rgba({{primaryColorRgb}}, 0.2) !important;
        border-left-color: {{primaryColor}} !important;
      }
      
      .spinner-border {
        border-color: rgba({{primaryColorRgb}}, 0.2);
        border-left-color: {{primaryColor}};
      }
      
      /* Custom spinner animation */
      @keyframes brandSpinner {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      .loading-spinner {
        animation: brandSpinner 1s linear infinite;
        border: 3px solid rgba({{primaryColorRgb}}, 0.2);
        border-left-color: {{primaryColor}};
        border-radius: 50%;
        width: 24px;
        height: 24px;
      }
    `,
    variables: {
      primaryColor: '{{primaryColor}}',
      primaryColorRgb: '{{primaryColorRgb}}'
    },
    stackable: true
  },
  {
    id: 'loading-spinner-gradient',
    category: 'loadingSpinnerStyles',
    name: 'Gradient Spinner',
    description: 'Loading spinner with gradient colours',
    cssTemplate: `
      .blQQcr, .jVKOUv,
      .sc-bwzfXH .bounce1,
      .sc-bwzfXH .bounce2,
      .sc-bwzfXH .bounce3 {
        background: linear-gradient(45deg, {{primaryColor}}, {{secondaryColor}}) !important;
        animation: gradientBounce 1.4s ease-in-out infinite both;
      }
      
      @keyframes gradientBounce {
        0%, 80%, 100% { 
          transform: scale(0);
          background: {{primaryColor}};
        }
        40% { 
          transform: scale(1.0);
          background: {{secondaryColor}};
        }
      }
      
      .sc-bwzfXH .bounce1 { animation-delay: -0.32s; }
      .sc-bwzfXH .bounce2 { animation-delay: -0.16s; }
    `,
    variables: {
      primaryColor: '{{primaryColor}}',
      secondaryColor: '{{secondaryColor}}'
    },
    stackable: true
  }
];

// Search Button Effects Templates (Multi-Selection - Search button animations)
export const searchButtonEffects: ComponentTemplate[] = [
  {
    id: 'search-button-enhanced',
    category: 'searchButtonEffects',
    name: 'Enhanced Search Button',
    description: 'Improved search button with brand styling',
    cssTemplate: `
      .MenuSearchButton__SearchButton-sc-1ph0p6o-0,
      .HTDQf {
        transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
        background: {{searchButtonRestBackgroundColor}};
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex !important;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 10px rgba({{searchButtonRestBackgroundColorRgb}}, 0.3);
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      .MenuSearchButton__SearchButton-sc-1ph0p6o-0 svg,
      .HTDQf svg {
        width: 20px;
        height: 20px;
        color: {{searchButtonRestSvgColor}} !important;
        transition: all 0.3s ease;
      }
      
      .MenuSearchButton__SearchButton-sc-1ph0p6o-0:hover,
      .HTDQf:hover {
        transform: scale(1.2) rotate(15deg);
        background: {{searchButtonHoverBackgroundColor}};
        box-shadow: 0 4px 15px rgba({{searchButtonHoverBackgroundColorRgb}}, 0.4);
      }
      
      .MenuSearchButton__SearchButton-sc-1ph0p6o-0:hover svg,
      .HTDQf:hover svg {
        color: {{searchButtonHoverSvgColor}} !important;
        transform: rotate(15deg);
      }
    `,
    variables: {
      searchButtonRestBackgroundColor: '{{searchButtonRestBackgroundColor}}',
      searchButtonRestSvgColor: '{{searchButtonRestSvgColor}}',
      searchButtonHoverBackgroundColor: '{{searchButtonHoverBackgroundColor}}',
      searchButtonHoverSvgColor: '{{searchButtonHoverSvgColor}}',
      searchButtonRestBackgroundColorRgb: '{{searchButtonRestBackgroundColorRgb}}',
      searchButtonHoverBackgroundColorRgb: '{{searchButtonHoverBackgroundColorRgb}}'
    },
    stackable: true
  },
  {
    id: 'search-button-pulse',
    category: 'searchButtonEffects',
    name: 'Pulsing Search Button',
    description: 'Search button with pulsing animation',
    cssTemplate: `
      .MenuSearchButton__SearchButton-sc-1ph0p6o-0,
      .HTDQf {
        animation: searchPulse 2s ease-in-out infinite;
      }
      
      @keyframes searchPulse {
        0%, 100% { 
          box-shadow: 0 2px 10px rgba({{primaryColorRgb}}, 0.3);
          transform: scale(1);
        }
        50% { 
          box-shadow: 0 4px 20px rgba({{primaryColorRgb}}, 0.5);
          transform: scale(1.05);
        }
      }
    `,
    variables: {
      primaryColorRgb: '{{primaryColorRgb}}'
    },
    stackable: true
  }
];

// Social Icon Effects Templates (Multi-Selection - Footer social icon animations)
export const socialIconEffects: ComponentTemplate[] = [
  {
    id: 'social-icons-enhanced',
    category: 'socialIconEffects',
    name: 'Enhanced Social Icons',
    description: 'Improved hover effects for social media icons',
    cssTemplate: `
      .components__SocialIcons-sc-1vb6jo6-3 a,
      .footer-social-icons a {
        transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
        display: inline-block;
        padding: 8px;
        border-radius: 50%;
        background: rgba({{primaryColorRgb}}, 0.1);
        margin: 0 5px;
      }
      
      .components__SocialIcons-sc-1vb6jo6-3 a:hover,
      .footer-social-icons a:hover {
        transform: translateY(-3px) rotate(-5deg) scale(1.1);
        color: {{primaryColor}} !important;
        background: rgba({{primaryColorRgb}}, 0.2);
        box-shadow: 0 4px 15px rgba({{primaryColorRgb}}, 0.3);
      }
    `,
    variables: {
      primaryColor: '{{primaryColor}}',
      primaryColorRgb: '{{primaryColorRgb}}'
    },
    stackable: true
  },
];


// Scroll Based Effects Templates (Multi-Selection - Navigation changes on scroll)
export const scrollBasedEffects: ComponentTemplate[] = [
  {
    id: 'scroll-fade-elements',
    category: 'scrollBasedEffects',
    name: 'Fade Elements on Scroll',
    description: 'Elements fade in as they come into view',
    jsTemplate: `
      // Intersection Observer for scroll-based animations
      const observerOptions = {
        root: null,
        rootMargin: '0px 0px -100px 0px',
        threshold: 0.1
      };
      
      const fadeObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
            fadeObserver.unobserve(entry.target);
          }
        });
      }, observerOptions);
      
      // Apply to various elements
      document.querySelectorAll('.item__DishComponent-wkeq8p-0, .components__ServiceInfo-a2rvuw-0, .components__Wrapper-sc-1vb6jo6-0').forEach(element => {
        element.style.opacity = '0';
        element.style.transform = 'translateY(30px)';
        element.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        fadeObserver.observe(element);
      });
    `,
    variables: {},
    stackable: true
  }
];

// Combine all templates
export const allComponentTemplates: ComponentTemplate[] = [
  ...menuItemMovement,
  ...menuItemGlow,
  ...menuItemImageEffects,
  ...menuItemTextEffects,
  ...categoryHeaderStyle,
  ...categoryHeaderAnimation,
  ...buttonHoverEffect,
  ...buttonClickEffect,
  ...buttonPulseEffect,
  ...initialFadeStyle,
  ...staggerTiming,
  ...navigationStyles,
  ...buttonEffects,
  ...serviceChoiceStyles,
  ...footerEnhancements,
  ...loadingAnimations,
  ...effectsEnhancements,
  ...animationsCombination,
  ...welcomeMessages,
  ...tagBadgeStyles,
  ...errorMessageStyles,
  ...loadingSpinnerStyles,
  ...searchButtonEffects,
  ...socialIconEffects,
  ...scrollBasedEffects
];

// Group templates by category (supporting both new and legacy categories)
export const componentsByCategory: Record<ComponentCategory, ComponentTemplate[]> = {
  // New granular categories
  menuItemMovement,
  menuItemGlow,
  menuItemImageEffects,
  menuItemTextEffects,
  categoryHeaderStyle,
  categoryHeaderAnimation,
  categoryHeaderTextEffects,
  buttonHoverEffect,
  buttonClickEffect,
  buttonPulseEffect,
  initialFadeStyle,
  staggerTiming,
  navigationStyles,
  serviceChoiceStyles,
  footerEnhancements,
  // Phase 2 new categories
  priceTagEnhancements,
  navBarTextEffects,
  welcomeMessages,
  tagBadgeStyles,
  errorMessageStyles,
  loadingSpinnerStyles,
  searchButtonEffects,
  socialIconEffects,
  scrollBasedEffects,
  // Legacy categories (map to existing arrays)
  menuItemAnimations: menuItemMovement, // Map legacy to new
  categoryHeaders: categoryHeaderStyle, // Map legacy to new
  buttonEffects,
  loadingAnimations,
  effectsEnhancements,
  animationsCombination
};

// Phone validator script (always appended to body)
export const phoneValidatorScript = `
<script>
// NZ/AUS Phone Validation - Final Production Version
// Validates: starts with 02 (9-11 digits) or 04 (exactly 10 digits)
// No debouncing - immediate validation

(function() {
  'use strict';
  
  // Add required CSS
  const style = document.createElement('style');
  style.textContent = \`
    .phone-validation-blocked {
      display: none !important;
    }
    
    .phone-error-message {
      color: #1ea72e;
      font-size: 14px;
      margin-top: 5px;
      padding: 5px 10px;
      background: rgba(30, 167, 46, 0.1);
      border-radius: 4px;
      border: 1px solid rgba(30, 167, 46, 0.3);
    }
    
    .phone-warning-message {
      background: rgba(255, 193, 7, 0.1);
      border: 1px solid rgba(255, 193, 7, 0.3);
      color: #ffc107;
      font-size: 14px;
      padding: 10px;
      margin: 10px 0;
      border-radius: 4px;
      text-align: center;
      font-weight: 600;
    }
    
    .PhoneInputInput.invalid {
      border-color: #1ea72e !important;
      background-color: rgba(30, 167, 46, 0.05) !important;
    }
  \`;
  document.head.appendChild(style);
  
  // SIMPLE validation - check prefix and appropriate length
  const validatePhone = (phoneNumber) => {
    // Remove all non-digit characters
    const cleanNumber = phoneNumber.replace(/\\D/g, '');
    
    // Check if empty
    if (!cleanNumber) {
      return { valid: false, error: 'Phone number is required' };
    }
    
    // Check if starts with 02 or 04
    if (!cleanNumber.startsWith('02') && !cleanNumber.startsWith('04')) {
      return { valid: false, error: 'Mobile number must start with 02 or 04' };
    }
    
    // Check length based on prefix
    if (cleanNumber.startsWith('04')) {
      // 04 numbers must be exactly 10 digits
      if (cleanNumber.length !== 10) {
        return { valid: false, error: \`(\${cleanNumber.length}/10)\` };
      }
    } else if (cleanNumber.startsWith('02')) {
      // 02 numbers can be 9-11 digits
      if (cleanNumber.length < 9) {
        return { valid: false, error: \`(\${cleanNumber.length}/9)\` };
      }
      if (cleanNumber.length > 11) {
        return { valid: false, error: \`(\${cleanNumber.length}/11)\` };
      }
    }
    
    return { valid: true };
  };
  
  // Main validation function
  const phoneValidation = () => {
    // Find phone input
    const phoneInput = document.querySelector('.PhoneInputInput');
    if (!phoneInput) {
      return;
    }
    
    // Check if validation is already set up
    if (phoneInput.hasAttribute('data-validation-setup')) {
      return;
    }
    phoneInput.setAttribute('data-validation-setup', 'true');
    
    // Create error element
    let errorElement = document.querySelector('.phone-error-message');
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'phone-error-message';
      errorElement.style.display = 'none';
      
      const phoneContainer = phoneInput.closest('.PhoneInput');
      if (phoneContainer) {
        phoneContainer.parentNode.insertBefore(errorElement, phoneContainer.nextSibling);
      }
    }
    
    // Create warning element
    let warningElement = null;
    
    // Toggle payment section
    const togglePaymentButtons = (show) => {
      const paymentSection = document.querySelector('[class*="checkout__PlaceOrderWrapper"]');
      
      if (paymentSection) {
        if (show) {
          paymentSection.classList.remove('phone-validation-blocked');
          // Remove any existing warning messages
          const existingWarnings = document.querySelectorAll('.phone-warning-message');
          existingWarnings.forEach(warning => warning.remove());
          if (warningElement) {
            warningElement = null;
          }
        } else {
          paymentSection.classList.add('phone-validation-blocked');
          
          // Remove any existing warning messages first
          const existingWarnings = document.querySelectorAll('.phone-warning-message');
          existingWarnings.forEach(warning => warning.remove());
          
          // Create new warning message
          warningElement = document.createElement('div');
          warningElement.className = 'phone-warning-message';
          warningElement.innerHTML = 'Please enter a valid mobile number to place your order';
          paymentSection.parentNode.insertBefore(warningElement, paymentSection);
        }
      }
    };
    
    // Handle validation - NO DEBOUNCING
    const handleValidation = () => {
      const phoneValue = phoneInput.value;
      const validation = validatePhone(phoneValue);
      
      if (validation.valid) {
        errorElement.style.display = 'none';
        phoneInput.classList.remove('invalid');
        togglePaymentButtons(true);
      } else {
        errorElement.textContent = validation.error;
        errorElement.style.display = 'block';
        phoneInput.classList.add('invalid');
        togglePaymentButtons(false);
      }
    };
    
    // Event handlers
    const inputHandler = (e) => {
      handleValidation();
    };
    
    const changeHandler = (e) => {
      handleValidation();
    };
    
    const blurHandler = (e) => {
      handleValidation();
    };
    
    const keyupHandler = (e) => {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        handleValidation();
      }
    };
    
    const pasteHandler = (e) => {
      setTimeout(() => {
        handleValidation();
      }, 0);
    };
    
    const cutHandler = (e) => {
      setTimeout(() => {
        handleValidation();
      }, 0);
    };
    
    // Set up ALL event listeners
    phoneInput.addEventListener('input', inputHandler);
    phoneInput.addEventListener('change', changeHandler);
    phoneInput.addEventListener('blur', blurHandler);
    phoneInput.addEventListener('keyup', keyupHandler);
    phoneInput.addEventListener('paste', pasteHandler);
    phoneInput.addEventListener('cut', cutHandler);
    
    // Check initial value
    if (phoneInput.value) {
      handleValidation();
    } else {
      togglePaymentButtons(false);
    }
  };
  
  // Modal observer
  const modalObserver = new MutationObserver((mutations) => {
    const checkoutModal = document.getElementById('checkout-modal');
    const phoneInput = document.querySelector('.PhoneInputInput');
    
    if (checkoutModal && checkoutModal.style.display !== 'none' && phoneInput) {
      phoneValidation();
    }
    
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        const addedNodes = Array.from(mutation.addedNodes);
        const hasPhoneInput = addedNodes.some(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            return node.querySelector && (
              node.querySelector('.PhoneInputInput') ||
              node.classList && node.classList.contains('PhoneInput')
            );
          }
          return false;
        });
        
        // Check if payment section was added (when Uber quote arrives)
        const hasPaymentSection = addedNodes.some(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            return node.querySelector && 
              node.querySelector('[class*="checkout__PlaceOrderWrapper"]') ||
              (node.className && typeof node.className === 'string' && 
               node.className.includes('checkout__PlaceOrderWrapper'));
          }
          return false;
        });
        
        if (hasPhoneInput) {
          setTimeout(phoneValidation, 100);
        }
        
        // If payment section was added, trigger validation immediately
        if (hasPaymentSection && phoneInput) {
          const phoneInputElem = document.querySelector('.PhoneInputInput');
          if (phoneInputElem && phoneInputElem.hasAttribute('data-validation-setup')) {
            // Validation is already set up, just trigger the handler
            const validation = validatePhone(phoneInputElem.value);
            const errorElement = document.querySelector('.phone-error-message');
            const paymentSection = document.querySelector('[class*="checkout__PlaceOrderWrapper"]');
            
            if (!validation.valid && paymentSection) {
              // Hide payment section immediately
              paymentSection.classList.add('phone-validation-blocked');
              
              // Remove any existing warning messages first
              const existingWarnings = document.querySelectorAll('.phone-warning-message');
              existingWarnings.forEach(warning => warning.remove());
              
              // Add new warning message
              const warningElement = document.createElement('div');
              warningElement.className = 'phone-warning-message';
              warningElement.innerHTML = 'Please enter a valid mobile number to place your order';
              paymentSection.parentNode.insertBefore(warningElement, paymentSection);
              
              // Show error message
              if (errorElement) {
                errorElement.textContent = validation.error;
                errorElement.style.display = 'block';
              }
              phoneInputElem.classList.add('invalid');
            } else if (validation.valid && paymentSection) {
              // Phone is valid, make sure payment section is visible
              paymentSection.classList.remove('phone-validation-blocked');
              
              // Remove any existing warning messages
              const existingWarnings = document.querySelectorAll('.phone-warning-message');
              existingWarnings.forEach(warning => warning.remove());
              
              // Hide error message
              if (errorElement) {
                errorElement.style.display = 'none';
              }
              phoneInputElem.classList.remove('invalid');
            }
          } else {
            // Validation not set up yet, initialize it
            setTimeout(phoneValidation, 100);
          }
        }
      }
    }
  });
  
  modalObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(phoneValidation, 500);
  });
  
})();
</script>`;